import React, { useState, useEffect, useRef, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Sparkles, Image as ImageIcon, Wand2, Palette, Upload, Eye, Save, Plus, Trash2, Pencil,
  Loader2, ArrowLeftRight, Package, Calculator, X, Check, Utensils, Briefcase, ShoppingBag,
  RefreshCw, Download, FileText, Settings, ListChecks, ChevronUp, ChevronDown, History, RotateCcw,
  Clipboard, Globe, Layers, Lightbulb, Coins, Route, Play
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import AiProgressBar from "@/components/AiProgressBar";
import { Separator } from "@/components/ui/separator";
import { compressImageDataUrl, b64Size } from "./utils/imageCompress";
import { ReactPhotoSphereViewer } from "react-photo-sphere-viewer";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TEMPLATE_ICONS = { ristorante: Utensils, ufficio: Briefcase, boutique: ShoppingBag };
const AREAS = ["pareti", "pavimento", "soffitto", "porte", "infissi"];

const fileToBase64 = (file) => new Promise((res, rej) => {
  const r = new FileReader();
  r.onload = () => res(r.result);
  r.onerror = rej;
  r.readAsDataURL(file);
});

const downloadPdf = async (url, name) => {
  try {
    const res = await axios.get(url, { responseType: "blob" });
    const u = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
    const a = document.createElement("a");
    a.href = u; a.download = name || "documento.pdf";
    document.body.appendChild(a); a.click(); a.remove();
    window.URL.revokeObjectURL(u);
  } catch { toast.error("Errore download PDF"); }
};

const downloadXlsx = async (url, name) => {
  try {
    const res = await axios.get(url, { responseType: "blob" });
    const u = window.URL.createObjectURL(new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }));
    const a = document.createElement("a");
    a.href = u; a.download = name || "documento.xlsx";
    document.body.appendChild(a); a.click(); a.remove();
    window.URL.revokeObjectURL(u);
  } catch { toast.error("Errore download Excel"); }
};


// ====================== EDITOR PROGETTO ======================
const ProgettoEditor = ({ progetto, onClose, onUpdated, authToken, voci }) => {
  const [data, setData] = useState(progetto);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("render");
  const [templates, setTemplates] = useState([]);

  // Render controls
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [customPrompt, setCustomPrompt] = useState("");
  const [extraNote, setExtraNote] = useState("");
  const [hexColor, setHexColor] = useState("");
  const [targetArea, setTargetArea] = useState("");
  const [generating, setGenerating] = useState(false);

  // Object proposals
  const [proposals, setProposals] = useState(null);
  const [analyzingObj, setAnalyzingObj] = useState(false);

  // Uploaded image for "Analisi inventario da foto reale" (Inventario Progetto tab)
  const [analysisImage, setAnalysisImage] = useState(null);  // { content_base64, nome_file }
  const analysisFileRef = useRef(null);

  // Color
  const [nomeColore, setNomeColore] = useState("");

  // Prompt preview
  const [foto360PreviewOpen, setFoto360PreviewOpen] = useState(false);
  const [renderPreview360, setRenderPreview360] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [quality, setQuality] = useState("standard"); // 'bozza' | 'standard' | 'alta' | 'ultra' | 'ultra_pro'
  const [preserveText, setPreserveText] = useState(true);
  const [analyzeOpen, setAnalyzeOpen] = useState(false);
  const [analyzeLoading, setAnalyzeLoading] = useState(false);
  const [analyzeResult, setAnalyzeResult] = useState(null);
  // Preservation mask (Feb 2026): brush-drawn regions to preserve verbatim from foto_base
  const [maskDialogOpen, setMaskDialogOpen] = useState(false);
  const [maskDataUrl, setMaskDataUrl] = useState("");  // black+white png data URL saved after draw
  const [maskBrushSize, setMaskBrushSize] = useState(30);
  const maskCanvasRef = useRef(null);
  const maskImgRef = useRef(null);
  const maskDrawingRef = useRef({ drawing: false, mode: "add" });

  // Templates manager
  const [tplDialogOpen, setTplDialogOpen] = useState(false);

  // Multi-foto / 360 / style refs (NEW)
  const [foto360, setFoto360] = useState(false);
  const [savingAsPano, setSavingAsPano] = useState(false);
  const [riferimentiStile, setRiferimentiStile] = useState([]); // volatile, max 5
  const [resyncInProgress, setResyncInProgress] = useState(false);

  // Cost tracking (estimation, session-only)
  const [sessionCost, setSessionCost] = useState(0);
  const [sessionRenders, setSessionRenders] = useState(0);

  // Remove object confirmation dialog
  const [removeObjDialog, setRemoveObjDialog] = useState({ open: false, oggetto: null, idx: -1 });

  // Computi
  const [computi, setComputi] = useState([]);
  const [editingComputo, setEditingComputo] = useState(null);
  const [computoDialogOpen, setComputoDialogOpen] = useState(false);

  // Inventory Lists (Blocco B, Feb 2026): named saved lists per progetto
  const [inventoryLists, setInventoryLists] = useState([]);
  const [saveListDialogOpen, setSaveListDialogOpen] = useState(false);
  const [saveListForm, setSaveListForm] = useState({ nome: "", descrizione: "", editing_id: null });

  // Refs for hidden file inputs
  const fileRef = useRef(null);
  const fileExtraRef = useRef(null);
  const fileStileRef = useRef(null);

  // Gallery picker for foto_base (Feb 2026)
  const [galleryPickerOpen, setGalleryPickerOpen] = useState(false);
  const [galleryPickerItems, setGalleryPickerItems] = useState([]);
  const [galleryPickerLoading, setGalleryPickerLoading] = useState(false);
  const [galleryPickerFilter, setGalleryPickerFilter] = useState("all"); // 'all' | '2d' | '360' | 'recent'

  const openGalleryPickerForBase = async () => {
    setGalleryPickerOpen(true);
    setGalleryPickerLoading(true);
    try {
      const r = await axios.get(`${API}/design/gallery?token=${authToken}`);
      setGalleryPickerItems(r.data || []);
    } catch { toast.error("Errore caricamento galleria"); }
    finally { setGalleryPickerLoading(false); }
  };

  const pickFromGalleryAsBase = async (gid, isKeep360) => {
    setLoading(true);
    setGalleryPickerOpen(false);
    try {
      const r = await axios.get(`${API}/design/gallery/${gid}?token=${authToken}`);
      const item = r.data;
      // Save it as foto_base of the current progetto + persist foto_360 flag
      await axios.put(`${API}/design/progetti/${progetto.id}?token=${authToken}`, {
        foto_base: item.image_base64,
        foto_360: !!isKeep360 && !!item.is_360,
      });
      setFoto360(!!isKeep360 && !!item.is_360);
      await refreshProgetto();
      toast.success(`Foto base aggiornata da "${item.nome}"`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore importazione da galleria");
    } finally { setLoading(false); }
  };

  const loadTemplates = async () => {
    try {
      const r = await axios.get(`${API}/design/templates?token=${authToken}`);
      setTemplates(r.data || []);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    loadTemplates();
    refreshProgetto();
    loadComputi();
    loadInventoryLists();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refreshProgetto = async () => {
    try {
      const r = await axios.get(`${API}/design/progetti/${progetto.id}?token=${authToken}`);
      setData(r.data);
    } catch { /* ignore */ }
  };

  const loadComputi = async () => {
    try {
      const r = await axios.get(`${API}/computo/computi?progetto_id=${progetto.id}&token=${authToken}`);
      setComputi(r.data || []);
    } catch { /* ignore */ }
  };

  const loadInventoryLists = async () => {
    try {
      const r = await axios.get(`${API}/design/inventory-lists?progetto_id=${progetto.id}&token=${authToken}`);
      setInventoryLists(r.data || []);
    } catch { /* ignore */ }
  };

  // Save current oggetti as a named inventory list (or update existing)
  const openSaveListDialog = () => {
    if (!(data.oggetti || []).length) { toast.error("Inventario vuoto: nessun oggetto da salvare"); return; }
    const defaultNome = `Inventario ${data.nome} - ${new Date().toLocaleDateString("it-IT")}`;
    setSaveListForm({ nome: defaultNome, descrizione: "", editing_id: null });
    setSaveListDialogOpen(true);
  };

  const openUpdateListDialog = (lst) => {
    setSaveListForm({ nome: lst.nome, descrizione: lst.descrizione || "", editing_id: lst.id });
    setSaveListDialogOpen(true);
  };

  const handleSaveList = async () => {
    const nome = (saveListForm.nome || "").trim();
    if (!nome) { toast.error("Nome obbligatorio"); return; }
    const payload = {
      nome,
      descrizione: saveListForm.descrizione || "",
      progetto_id: progetto.id,
      oggetti: data.oggetti || [],
    };
    try {
      if (saveListForm.editing_id) {
        await axios.put(`${API}/design/inventory-lists/${saveListForm.editing_id}?token=${authToken}`, payload);
        toast.success(`Lista "${nome}" aggiornata`);
      } else {
        await axios.post(`${API}/design/inventory-lists?token=${authToken}`, payload);
        toast.success(`Lista "${nome}" salvata (${(data.oggetti || []).length} oggetti)`);
      }
      setSaveListDialogOpen(false);
      loadInventoryLists();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore salvataggio lista");
    }
  };

  const loadListToCurrent = async (lst) => {
    if (!window.confirm(`Caricare "${lst.nome}" (${(lst.oggetti || []).length} oggetti) come inventario corrente? Sostituirà l'inventario attuale.`)) return;
    try {
      await axios.post(`${API}/design/progetti/${progetto.id}/oggetti?token=${authToken}`, { oggetti: lst.oggetti || [] });
      await refreshProgetto();
      toast.success(`Lista "${lst.nome}" caricata`);
    } catch { toast.error("Errore caricamento lista"); }
  };

  const deleteInventoryList = async (lst) => {
    if (!window.confirm(`Eliminare la lista "${lst.nome}"?`)) return;
    try {
      await axios.delete(`${API}/design/inventory-lists/${lst.id}?token=${authToken}`);
      loadInventoryLists();
      toast.success("Lista eliminata");
    } catch { toast.error("Errore eliminazione"); }
  };

  const handleUploadFoto = async (file) => {
    if (!file) return;
    setLoading(true);
    try {
      const b64 = await fileToBase64(file);
      await axios.put(`${API}/design/progetti/${progetto.id}?token=${authToken}`, { foto_base: b64 });
      await refreshProgetto();
      toast.success("Foto caricata");
    } catch { toast.error("Errore upload"); }
    finally { setLoading(false); }
  };

  // Paste image from clipboard
  const handlePasteFromClipboard = async () => {
    try {
      if (!navigator.clipboard || !navigator.clipboard.read) {
        toast.error("Clipboard API non supportata da questo browser");
        return;
      }
      const items = await navigator.clipboard.read();
      for (const item of items) {
        for (const type of item.types) {
          if (type.startsWith("image/")) {
            const blob = await item.getType(type);
            const file = new File([blob], `pasted-${Date.now()}.png`, { type });
            await handleUploadFoto(file);
            return;
          }
        }
      }
      toast.error("Nessuna immagine trovata negli appunti");
    } catch (e) {
      toast.error("Impossibile leggere dagli appunti: autorizza il sito");
    }
  };

  // Add additional context views (saved to progetto.foto_aggiuntive on backend)
  const handleAddFotoExtra = async (files) => {
    if (!files || files.length === 0) return;
    const current = data.foto_aggiuntive || [];
    const newOnes = [];
    for (const f of Array.from(files)) {
      const b64 = await fileToBase64(f);
      newOnes.push({ id: `ext-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`, nome_file: f.name, content_base64: b64 });
    }
    const updated = [...current, ...newOnes].slice(0, 6); // cap 6
    try {
      await axios.put(`${API}/design/progetti/${progetto.id}?token=${authToken}`, { foto_aggiuntive: updated });
      setData(prev => ({ ...prev, foto_aggiuntive: updated }));
      toast.success(`${newOnes.length} foto di contesto aggiunte`);
    } catch { toast.error("Errore upload"); }
  };

  const handleRemoveFotoExtra = async (idx) => {
    const updated = (data.foto_aggiuntive || []).filter((_, i) => i !== idx);
    try {
      await axios.put(`${API}/design/progetti/${progetto.id}?token=${authToken}`, { foto_aggiuntive: updated });
      setData(prev => ({ ...prev, foto_aggiuntive: updated }));
    } catch { toast.error("Errore"); }
  };

  // Style references (volatile, in component state only)
  const handleAddRiferimenti = async (files) => {
    if (!files || files.length === 0) return;
    const newOnes = [];
    for (const f of Array.from(files)) {
      const b64 = await fileToBase64(f);
      newOnes.push({ id: `ref-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`, nome_file: f.name, content_base64: b64 });
    }
    setRiferimentiStile(prev => [...prev, ...newOnes].slice(0, 5));
  };

  // Set foto_360 flag persistently
  useEffect(() => {
    if (data.foto_360 !== undefined) setFoto360(!!data.foto_360);
  }, [data.foto_360]);

  // Global Ctrl+V to paste image (only on render tab and no input focused)
  useEffect(() => {
    const onPaste = async (e) => {
      if (activeTab !== "render") return;
      const target = e.target;
      if (target && /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName)) return;
      const items = e.clipboardData?.items || [];
      for (const it of items) {
        if (it.type?.startsWith("image/")) {
          const blob = it.getAsFile();
          if (blob) {
            e.preventDefault();
            await handleUploadFoto(blob);
            return;
          }
        }
      }
    };
    window.addEventListener("paste", onPaste);
    return () => window.removeEventListener("paste", onPaste);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, progetto.id]);

  const handleGenerate = async () => {
    if (!data.foto_base) { toast.error("Carica prima una foto"); return; }
    if (!selectedTemplate && !customPrompt.trim() && !hexColor && !nomeColore.trim() && !extraNote.trim()) {
      toast.error("Specifica un template, un prompt, un colore o una nota");
      return;
    }
    setGenerating(true);
    try {
      const body = {
        template: selectedTemplate || null,
        prompt: customPrompt || null,
        note: extraNote || null,
        hex_color: hexColor || null,
        nome_colore: nomeColore || null,
        target_area: targetArea || null,
        foto_360: foto360,
        quality,
        preserve_text: preserveText,
        preservation_mask_base64: maskDataUrl || null,
        foto_aggiuntive: (data.foto_aggiuntive || []).map(f => f.content_base64 || f),
        riferimenti_stile: riferimentiStile.map(r => r.content_base64),
      };
      const r = await axios.post(`${API}/design/progetti/${progetto.id}/genera-render?token=${authToken}`, body);
      // Update in place WITHOUT reloading parent (so user stays on the project)
      setData(prev => ({
        ...prev,
        render_corrente: r.data.render,
        prompt_usato: r.data.prompt_usato,
        foto_360: foto360,
        prompt_history: r.data.history_entry
          ? [r.data.history_entry, ...(prev.prompt_history || [])]
          : (prev.prompt_history || []),
      }));
      // Riferimenti stile sono volatili: si svuotano dopo la generazione
      setRiferimentiStile([]);
      addToSessionCost(estimatedCost);
      window.dispatchEvent(new Event("wallet:refresh"));
      toast.success(`Render generato! Costo stimato: $${estimatedCost.toFixed(3)}`);
    } catch (e) {
      const raw = e.response?.data?.detail;
      let msg = "Errore generazione render";
      let refunded = 0;
      if (raw && typeof raw === "object") {
        msg = raw.error || msg;
        refunded = Number(raw.refunded_credits || 0);
      } else if (typeof raw === "string") {
        msg = raw;
      }
      if (e.response?.status === 402) {
        toast.error(msg);
        window.dispatchEvent(new Event("wallet:open-recharge"));
      } else {
        toast.error(msg);
      }
      if (refunded > 0) {
        toast.success(`💰 Crediti rimborsati automaticamente: $${refunded.toFixed(3)}`, { duration: 6000 });
        window.dispatchEvent(new Event("wallet:refresh"));
      }
    } finally { setGenerating(false); }
  };

  const handleSaveAsPanorama = async () => {
    if (!data.render_corrente) return;
    setSavingAsPano(true);
    try {
      // Strip any previous " – DD/MM HH:MM" timestamp (progetti "Rifinitura" hanno la data congelata)
      const baseName = (data.nome || "Panorama").replace(/\s+[–-]\s+\d{2}\/\d{2}\s+\d{2}:\d{2}\s*$/, "").trim();
      const now = new Date();
      const stamp = `${String(now.getDate()).padStart(2, "0")}/${String(now.getMonth() + 1).padStart(2, "0")} ${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;
      const r = await axios.post(`${API}/panorami/upload?token=${authToken}`, {
        nome: `${baseName} — ${stamp} — 360°`,
        note: `Generato da AI Design Assistant. Prompt: ${data.prompt_usato?.substring(0, 300) || ""}`,
        image_base64: data.render_corrente,
      });
      toast.success("Panorama salvato! Aprendo il viewer...", { duration: 2000 });
      setTimeout(() => {
        window.location.href = `${window.location.origin}${window.location.pathname}?panorama=${r.data.id}`;
      }, 800);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore salvataggio panorama");
    } finally { setSavingAsPano(false); }
  };


  const handleAnalyzeObjects = async () => {
    const hasUploaded = !!analysisImage?.content_base64;
    if (!hasUploaded && !data.render_corrente) {
      toast.error("Genera prima un render o carica una foto da analizzare");
      return;
    }
    setAnalyzingObj(true);
    try {
      // If user picked "Usa render", the backend already has render_corrente → skip the heavy upload.
      const useExistingRender = hasUploaded && analysisImage?.from_render === true;
      const body = (hasUploaded && !useExistingRender) ? { image_base64: analysisImage.content_base64 } : {};
      // Long-running vision call (15-60s) — give axios a generous timeout (2.5 min) so the proxy
      // is the only place that can hard-cancel.
      const r = await axios.post(
        `${API}/design/progetti/${progetto.id}/analizza-oggetti?token=${authToken}`,
        body,
        { timeout: 150000 }
      );
      const items = (r.data.proposals || []).map(o => ({ ...o, _selected: true }));
      setProposals(items);
      toast.success(`Trovati ${items.length} oggetti`);
    } catch (e) {
      const detail = e.response?.data?.detail || e.message || "Errore analisi";
      if (e.response?.status === 402) {
        toast.error(detail);
        window.dispatchEvent(new Event("wallet:open-recharge"));
      } else if (e.code === "ECONNABORTED" || /timeout/i.test(detail)) {
        toast.error("L'analisi ha impiegato troppo tempo. Riprova con un'immagine più piccola.");
      } else {
        toast.error(detail);
      }
    } finally { setAnalyzingObj(false); }
  };

  // Upload a custom image for inventory analysis (replaces existing previewed image)
  const handleUploadAnalysisImage = async (file) => {
    if (!file) return;
    try {
      const b64 = await fileToBase64(file);
      setAnalysisImage({ content_base64: b64, nome_file: file.name });
      toast.success("Foto caricata. Clicca 'Analizza con AI' per identificare gli oggetti.");
    } catch {
      toast.error("Errore lettura file");
    }
  };

  const handlePasteAnalysisImage = async () => {
    try {
      if (!navigator.clipboard || !navigator.clipboard.read) {
        toast.error("Clipboard API non supportata"); return;
      }
      const items = await navigator.clipboard.read();
      for (const item of items) {
        for (const type of item.types) {
          if (type.startsWith("image/")) {
            const blob = await item.getType(type);
            const b64 = await new Promise((res) => {
              const reader = new FileReader();
              reader.onloadend = () => res(reader.result);
              reader.readAsDataURL(blob);
            });
            setAnalysisImage({ content_base64: b64, nome_file: `clipboard.${type.split("/")[1] || "png"}` });
            toast.success("Immagine incollata");
            return;
          }
        }
      }
      toast.error("Nessuna immagine negli appunti");
    } catch {
      toast.error("Impossibile leggere dagli appunti");
    }
  };

  const toggleProposal = (idx) => setProposals(p => p.map((x, i) => i === idx ? { ...x, _selected: !x._selected } : x));
  const updateProposal = (idx, field, val) => setProposals(p => p.map((x, i) => i === idx ? { ...x, [field]: val } : x));
  const removeProposal = (idx) => setProposals(p => p.filter((_, i) => i !== idx));

  const handleSaveProposals = async () => {
    if (!proposals) return;
    const accepted = proposals.filter(p => p._selected).map(p => ({
      id: p.id,
      descrizione: p.descrizione,
      categoria: p.categoria || "",
      quantita: parseInt(p.quantita) || 1,
      prezzo_stimato: parseFloat(p.prezzo_stimato) || 0,
      fornitore: p.fornitore || "",
      link_catalogo: p.link_catalogo || "",
      confermato: true,
    }));
    // Merge with existing confermati
    const existing = (data.oggetti || []).filter(o => o.confermato);
    const merged = [...existing, ...accepted];
    try {
      await axios.post(`${API}/design/progetti/${progetto.id}/oggetti?token=${authToken}`, { oggetti: merged });
      setProposals(null);
      await refreshProgetto();
      onUpdated?.();
      toast.success(`Salvati ${accepted.length} oggetti nel progetto`);
    } catch { toast.error("Errore salvataggio"); }
  };

  // Trigger confirmation dialog instead of immediate delete
  const removeOggetto = (idx) => {
    const oggetto = (data.oggetti || [])[idx];
    if (!oggetto) return;
    setRemoveObjDialog({ open: true, oggetto, idx });
  };

  // Confirmed: delete from inventario only OR also from render
  const confirmRemoveOggetto = async (alsoFromRender) => {
    const { idx, oggetto } = removeObjDialog;
    setRemoveObjDialog({ open: false, oggetto: null, idx: -1 });
    if (idx < 0 || !oggetto) return;
    const updated = (data.oggetti || []).filter((_, i) => i !== idx);
    try {
      await axios.post(`${API}/design/progetti/${progetto.id}/oggetti?token=${authToken}`, { oggetti: updated });
      setData(prev => ({ ...prev, oggetti: updated }));
      toast.success("Oggetto rimosso dall'inventario");
    } catch { toast.error("Errore inventario"); return; }

    if (alsoFromRender && data.render_corrente) {
      setResyncInProgress(true);
      try {
        const r = await axios.post(
          `${API}/design/progetti/${progetto.id}/rimuovi-oggetto-render?token=${authToken}`,
          { descrizione: oggetto.descrizione }
        );
        setData(prev => ({
          ...prev,
          render_corrente: r.data.render,
          prompt_usato: r.data.prompt_usato || prev.prompt_usato,
          prompt_history: r.data.history_entry
            ? [r.data.history_entry, ...(prev.prompt_history || [])]
            : (prev.prompt_history || []),
        }));
        toast.success("Render aggiornato (oggetto rimosso)");
        addToSessionCost(0.04);
      } catch (e) {
        toast.error(e.response?.data?.detail || "Errore rimozione dal render");
      } finally { setResyncInProgress(false); }
    }
  };

  // Re-generate render based on current full inventory
  const handleResyncRender = async () => {
    if (!(data.oggetti || []).length) { toast.error("Inventario vuoto"); return; }
    if (!window.confirm(`Rigenera il render basandolo sui ${data.oggetti.length} oggetti correnti dell'inventario? Questo consumerà 1 credito AI.`)) return;
    setResyncInProgress(true);
    try {
      const r = await axios.post(
        `${API}/design/progetti/${progetto.id}/rigenera-da-inventario?token=${authToken}`,
        {}
      );
      setData(prev => ({
        ...prev,
        render_corrente: r.data.render,
        prompt_usato: r.data.prompt_usato || prev.prompt_usato,
        prompt_history: r.data.history_entry
          ? [r.data.history_entry, ...(prev.prompt_history || [])]
          : (prev.prompt_history || []),
      }));
      toast.success("Render rigenerato coerente con inventario");
      addToSessionCost(0.04);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore rigenerazione");
    } finally { setResyncInProgress(false); }
  };

  const totaleOggetti = useMemo(() => {
    return (data.oggetti || []).reduce((s, o) => s + (parseFloat(o.prezzo_stimato) || 0) * (parseInt(o.quantita) || 1), 0);
  }, [data.oggetti]);

  // Cost estimation per next render (Gemini Nano Banana approx)
  // Base: $0.04 per image generated. Each input image consumes input tokens.
  // Empirical estimate: $0.015 per extra input image (vision tokens).
  const estimatedCost = useMemo(() => {
    const base = { bozza: 0.03, standard: 0.05, alta: 0.08, ultra: 0.15, ultra_pro: 0.25 }[quality] || 0.05;
    let cost = base;
    const extras = (data.foto_aggiuntive || []).length + riferimentiStile.length;
    cost += extras * 0.015;
    if (foto360) cost += 0.01;
    return cost;
  }, [data.foto_aggiuntive, riferimentiStile, foto360, quality]);

  const handleAnalyzePhoto = async () => {
    if (!data.foto_base) { toast.error("Carica prima una foto base"); return; }
    setAnalyzeLoading(true);
    setAnalyzeResult(null);
    setAnalyzeOpen(true);
    try {
      const r = await axios.post(`${API}/design/progetti/${progetto.id}/analizza-foto-base?token=${authToken}`);
      setAnalyzeResult(r.data?.parsed || null);
      window.dispatchEvent(new Event("wallet:refresh"));
      if (!r.data?.parsed) {
        toast.error("Analisi non parseabile: mostro testo grezzo");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore analisi foto base");
      setAnalyzeOpen(false);
    } finally { setAnalyzeLoading(false); }
  };

  const addToSessionCost = (cost) => {
    setSessionCost(prev => prev + cost);
    setSessionRenders(prev => prev + 1);
  };

  // Live final-prompt preview (replica della logica backend)
  const finalPromptPreview = useMemo(() => {
    const parts = [];
    if (selectedTemplate) {
      const tpl = templates.find(t => t.id === selectedTemplate);
      if (tpl?.prompt) parts.push(tpl.prompt);
    }
    const nc = (nomeColore || "").trim();
    const hc = (hexColor || "").trim();
    const ta = (targetArea || "").trim();
    if (nc || hc) {
      let desc;
      if (nc && hc) desc = `colore ${nc} (riferimento ${hc})`;
      else if (nc) desc = `colore ${nc}`;
      else desc = `colore esadecimale ${hc}`;
      if (ta) parts.push(`Ridipingi ${ta} con il ${desc}.`);
      else parts.push(`Cambia il colore principale dell'ambiente nel ${desc}.`);
    }
    if (customPrompt?.trim()) parts.push(customPrompt.trim());
    if (extraNote?.trim()) parts.push(`Nota aggiuntiva: ${extraNote.trim()}`);
    return parts.join("\n\n").trim();
  }, [selectedTemplate, templates, nomeColore, hexColor, targetArea, customPrompt, extraNote]);

  // Apply a history entry into the form (Riusa)
  const applyHistoryEntry = (entry) => {    setSelectedTemplate(entry.template_id || "");
    setCustomPrompt(entry.custom_prompt || "");
    setExtraNote(entry.note || "");
    setHexColor(entry.hex_color || "");
    setNomeColore(entry.nome_colore || "");
    setTargetArea(entry.target_area || "");
    setPreviewOpen(true);
    toast.success("Parametri caricati. Modifica e rigenera.");
    // scroll to top of render tab
    setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 100);
  };

  const handleDeleteHistory = async (entryId) => {
    try {
      await axios.delete(`${API}/design/progetti/${progetto.id}/prompt-history/${entryId}?token=${authToken}`);
      await refreshProgetto();
    } catch { toast.error("Errore eliminazione"); }
  };

  // Computi handlers
  const openNewComputo = () => {
    setEditingComputo({
      nome: `Computo ${data.nome}`,
      descrizione: "",
      progetto_id: progetto.id,
      righe: [],
      note: "",
    });
    setComputoDialogOpen(true);
  };
  const openEditComputo = (c) => { setEditingComputo(c); setComputoDialogOpen(true); };
  const handleSaveComputo = async (c) => {
    try {
      if (c.id) await axios.put(`${API}/computo/computi/${c.id}?token=${authToken}`, c);
      else await axios.post(`${API}/computo/computi?token=${authToken}`, c);
      setComputoDialogOpen(false);
      loadComputi();
      toast.success("Computo salvato");
    } catch { toast.error("Errore"); }
  };
  const handleDeleteComputo = async (id) => {
    if (!window.confirm("Eliminare il computo?")) return;
    try {
      await axios.delete(`${API}/computo/computi/${id}?token=${authToken}`);
      loadComputi();
    } catch { toast.error("Errore"); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <Button variant="ghost" onClick={onClose} className="mb-2">← Torna ai progetti</Button>
          <h2 className="text-2xl font-bold flex items-center gap-2 text-indigo-700">
            <Sparkles className="h-6 w-6" /> {data.nome}
          </h2>
          {data.descrizione && <p className="text-sm text-gray-500">{data.descrizione}</p>}
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-indigo-50">
          <TabsTrigger value="render" data-testid="da-tab-render" className="data-[state=active]:bg-indigo-600 data-[state=active]:text-white"><Wand2 className="h-4 w-4 mr-1" /> Genera Render</TabsTrigger>
          <TabsTrigger value="sidebyside" data-testid="da-tab-sidebyside" className="data-[state=active]:bg-indigo-600 data-[state=active]:text-white"><ArrowLeftRight className="h-4 w-4 mr-1" /> Prima/Dopo</TabsTrigger>
          <TabsTrigger value="oggetti" data-testid="da-tab-oggetti" className="data-[state=active]:bg-indigo-600 data-[state=active]:text-white"><Package className="h-4 w-4 mr-1" /> Inventario Progetto</TabsTrigger>
          <TabsTrigger value="computo" data-testid="da-tab-computo" className="data-[state=active]:bg-indigo-600 data-[state=active]:text-white"><Calculator className="h-4 w-4 mr-1" /> Computo Metrico</TabsTrigger>
        </TabsList>

        {/* RENDER */}
        <TabsContent value="render" className="space-y-4 mt-4">
          <Card>
            <CardHeader><CardTitle className="text-base flex items-center gap-2"><ImageIcon className="h-4 w-4" /> Foto base</CardTitle></CardHeader>
            <CardContent>
              {data.foto_base ? (
                <div className="space-y-2">
                  <div className="relative inline-block">
                    <img src={data.foto_base} alt="base" className="max-h-80 rounded-lg border" />
                    {foto360 && (
                      <button
                        type="button"
                        onClick={() => setFoto360PreviewOpen(true)}
                        className="absolute inset-0 flex items-center justify-center bg-black/0 hover:bg-black/40 transition group rounded-lg"
                        title="Anteprima navigabile 360°"
                        data-testid="da-foto-base-play-360"
                      >
                        <span className="opacity-0 group-hover:opacity-100 bg-amber-500/95 text-white rounded-full p-3 shadow-xl transition">
                          <Play className="h-8 w-8" fill="white" />
                        </span>
                        <Badge className="absolute top-2 right-2 bg-amber-500 text-white border-0 shadow">
                          <Globe className="h-3 w-3 mr-1" /> 360°
                        </Badge>
                      </button>
                    )}
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()} data-testid="da-btn-replace-foto"><Upload className="h-4 w-4 mr-1" /> Sostituisci foto</Button>
                    <Button size="sm" variant="outline" onClick={handlePasteFromClipboard} data-testid="da-btn-paste"><Clipboard className="h-4 w-4 mr-1" /> Incolla da clipboard</Button>
                    <Button size="sm" variant="outline" onClick={openGalleryPickerForBase} className="border-indigo-300 text-indigo-700 hover:bg-indigo-50" data-testid="da-btn-gallery-base"><Sparkles className="h-4 w-4 mr-1" /> Scegli da Galleria</Button>
                    {foto360 && (
                      <Button size="sm" variant="outline" onClick={() => setFoto360PreviewOpen(true)} className="border-amber-400 text-amber-700 hover:bg-amber-50" data-testid="da-foto-base-play-btn">
                        <Play className="h-4 w-4 mr-1" /> Anteprima 360°
                      </Button>
                    )}
                    <label className="flex items-center gap-1.5 text-xs text-gray-700 px-2 py-1 bg-gray-50 rounded border cursor-pointer" data-testid="da-toggle-360">
                      <input type="checkbox" checked={foto360} onChange={async (e) => {
                        const v = e.target.checked;
                        setFoto360(v);
                        try { await axios.put(`${API}/design/progetti/${progetto.id}?token=${authToken}`, { foto_360: v }); } catch { /* ignore */ }
                      }} className="rounded" />
                      <Globe className="h-3.5 w-3.5" /> Foto 360°
                    </label>
                  </div>
                </div>
              ) : (
                <div>
                  <div onClick={() => fileRef.current?.click()} className="border-2 border-dashed border-gray-300 rounded-lg p-10 text-center cursor-pointer hover:bg-gray-50" data-testid="da-upload-foto">
                    <Upload className="h-10 w-10 mx-auto text-gray-400 mb-2" />
                    <p className="text-gray-600">Carica una foto dell&apos;ambiente da trasformare</p>
                  </div>
                  <div className="flex gap-2 mt-2 justify-center">
                    <Button size="sm" variant="outline" onClick={handlePasteFromClipboard} data-testid="da-btn-paste-empty"><Clipboard className="h-4 w-4 mr-1" /> Incolla da clipboard</Button>
                    <Button size="sm" variant="outline" onClick={openGalleryPickerForBase} className="border-indigo-300 text-indigo-700 hover:bg-indigo-50" data-testid="da-btn-gallery-base-empty">
                      <Sparkles className="h-4 w-4 mr-1" /> Scegli da Galleria AI
                    </Button>
                  </div>
                </div>
              )}
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => handleUploadFoto(e.target.files?.[0])} />
            </CardContent>
          </Card>

          {/* Foto aggiuntive di contesto */}
          {data.foto_base && (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <CardTitle className="text-base flex items-center gap-2"><Layers className="h-4 w-4" /> Foto di contesto ({(data.foto_aggiuntive || []).length})</CardTitle>
                  <Button size="sm" variant="outline" onClick={() => fileExtraRef.current?.click()} disabled={(data.foto_aggiuntive || []).length >= 6} data-testid="da-btn-add-extra">
                    <Plus className="h-4 w-4 mr-1" /> Aggiungi
                  </Button>
                </div>
                <CardDescription>Foto extra dello stesso ambiente (max 6): aiutano l&apos;AI a capire meglio lo spazio. Inviate insieme alla foto base ad ogni render.</CardDescription>
              </CardHeader>
              <CardContent>
                {(data.foto_aggiuntive || []).length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-3">Nessuna foto di contesto. (Opzionale)</p>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-2">
                    {(data.foto_aggiuntive || []).map((f, i) => (
                      <div key={f.id || i} className="relative group">
                        <img src={f.content_base64} alt={f.nome_file} className="w-full h-20 object-cover rounded border" />
                        <button onClick={() => handleRemoveFotoExtra(i)} className="absolute top-1 right-1 bg-red-600/80 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100">
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <input ref={fileExtraRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => handleAddFotoExtra(e.target.files)} />
              </CardContent>
            </Card>
          )}

          {/* Riferimenti stile (volatili) */}
          {data.foto_base && (
            <Card className="border-purple-200 bg-purple-50/30">
              <CardHeader>
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <CardTitle className="text-base flex items-center gap-2"><Lightbulb className="h-4 w-4" /> Riferimenti stile <Badge variant="secondary" className="text-xs">Volatili</Badge></CardTitle>
                  <Button size="sm" variant="outline" onClick={() => fileStileRef.current?.click()} disabled={riferimentiStile.length >= 5} data-testid="da-btn-add-refs">
                    <Plus className="h-4 w-4 mr-1" /> Aggiungi
                  </Button>
                </div>
                <CardDescription>Foto di brand/stile/mood da imitare (max 5). Usate SOLO per la prossima generazione, poi rimosse automaticamente.</CardDescription>
              </CardHeader>
              <CardContent>
                {riferimentiStile.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-3">Nessun riferimento. Esempio: foto di un negozio del brand che vuoi imitare.</p>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2">
                    {riferimentiStile.map((f, i) => (
                      <div key={f.id || i} className="relative group">
                        <img src={f.content_base64} alt={f.nome_file} className="w-full h-20 object-cover rounded border border-purple-300" />
                        <button onClick={() => setRiferimentiStile(prev => prev.filter((_, k) => k !== i))} className="absolute top-1 right-1 bg-red-600/80 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100">
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <input ref={fileStileRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => handleAddRiferimenti(e.target.files)} />
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader><CardTitle className="text-base flex items-center gap-2"><Wand2 className="h-4 w-4" /> Configurazione Render</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              {/* Templates */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <Label>Template Commerciale</Label>
                  <Button type="button" size="sm" variant="outline" onClick={() => setTplDialogOpen(true)} data-testid="da-btn-manage-templates">
                    <Settings className="h-4 w-4 mr-1" /> Gestisci Template
                  </Button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 mt-1">
                  {templates.map(t => {
                    const Icon = TEMPLATE_ICONS[t.id] || Sparkles;
                    const active = selectedTemplate === t.id;
                    return (
                      <button key={t.id} type="button" onClick={() => {
                        if (active) {
                          setSelectedTemplate("");
                          return;
                        }
                        const tplText = (t.prompt || "").trim();
                        if (customPrompt.trim() && tplText) {
                          const choice = window.confirm(
                            `Il campo Prompt personalizzato contiene già del testo.\n\nOK = SOSTITUISCI con il testo del template "${t.label}"\nAnnulla = mantieni il tuo prompt (il template si applica comunque insieme)`
                          );
                          if (choice) {
                            setCustomPrompt(tplText);
                            toast.success(`Prompt del template "${t.label}" caricato — puoi editarlo`);
                          }
                        } else if (tplText) {
                          setCustomPrompt(tplText);
                          toast.success(`Prompt del template "${t.label}" caricato — puoi editarlo`);
                        }
                        setSelectedTemplate(t.id);
                      }}
                        data-testid={`da-template-${t.id}`}
                        className={`flex items-center gap-2 p-3 rounded-lg border-2 transition ${active ? "border-indigo-600 bg-indigo-50" : "border-gray-200 hover:border-indigo-300"}`}>
                        <Icon className={`h-5 w-5 ${active ? "text-indigo-700" : "text-gray-500"}`} />
                        <div className="text-left flex-1">
                          <div className={`text-sm font-medium ${active ? "text-indigo-800" : "text-gray-700"}`}>{t.label}</div>
                        </div>
                        {active && <Check className="h-4 w-4 text-indigo-700" />}
                      </button>
                    );
                  })}
                  {templates.length === 0 && (
                    <div className="col-span-full text-center text-sm text-gray-500 py-3">Nessun template. Clicca &quot;Gestisci Template&quot; per aggiungerne.</div>
                  )}
                </div>
              </div>

              {/* Colore: nome libero + HEX picker + area target */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <Label className="flex items-center gap-1"><Palette className="h-4 w-4" /> Nome colore</Label>
                  <Input value={nomeColore} onChange={e => setNomeColore(e.target.value)} placeholder='Es. "rosa antico", "blu navy", "verde salvia"' data-testid="da-color-name" />
                </div>
                <div>
                  <Label>Colore HEX (opzionale)</Label>
                  <div className="flex gap-2">
                    <input type="color" value={hexColor || "#ffffff"} onChange={e => setHexColor(e.target.value)} className="h-10 w-12 rounded border shrink-0" data-testid="da-color-picker" />
                    <Input value={hexColor} onChange={e => setHexColor(e.target.value)} placeholder="#FAF0E6" className="font-mono" />
                  </div>
                </div>
                <div>
                  <Label>Area target</Label>
                  <Select value={targetArea || "none"} onValueChange={(v) => setTargetArea(v === "none" ? "" : v)}>
                    <SelectTrigger><SelectValue placeholder="Tutta l'immagine" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Tutta l&apos;immagine</SelectItem>
                      {AREAS.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Prompt custom - HIGHLIGHTED as the main input */}
              <div className="rounded-lg border-2 border-indigo-400 bg-gradient-to-br from-indigo-50 via-white to-purple-50 p-3 shadow-sm ring-1 ring-indigo-200/60">
                <div className="flex items-center justify-between gap-2 mb-1">
                  <Label className="text-indigo-800 font-semibold flex items-center gap-1">
                    <Wand2 className="h-4 w-4" /> Prompt personalizzato <span className="text-red-500">*</span>
                  </Label>
                  {(data.prompt_history || []).length > 0 && (
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button size="sm" variant="outline" className="h-7 text-xs border-indigo-300 text-indigo-700 hover:bg-indigo-100" data-testid="da-prev-prompts-btn">
                          <History className="h-3 w-3 mr-1" /> Prompt precedenti ({(data.prompt_history || []).length})
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent align="end" className="w-[380px] max-h-[420px] overflow-y-auto p-2" data-testid="da-prev-prompts-list">
                        <div className="text-xs font-semibold text-gray-700 px-2 py-1 mb-1 border-b">Clicca un prompt per riutilizzarlo</div>
                        {(data.prompt_history || []).map((h) => {
                          const preview = (h.custom_prompt || h.prompt_finale || "").slice(0, 180);
                          if (!preview.trim()) return null;
                          return (
                            <button
                              key={h.id}
                              type="button"
                              onClick={() => { setCustomPrompt(h.custom_prompt || h.prompt_finale || ""); toast.success("Prompt caricato"); }}
                              className="w-full text-left rounded p-2 hover:bg-indigo-50 border border-transparent hover:border-indigo-200 mb-1 group"
                              data-testid={`da-prev-prompt-${h.id}`}
                            >
                              <div className="flex items-center gap-1 mb-1 flex-wrap">
                                <Badge variant="secondary" className="text-[10px]">{new Date(h.timestamp).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}</Badge>
                                {h.template_id && <Badge className="bg-indigo-100 text-indigo-800 text-[10px]">{h.template_id}</Badge>}
                                {h.nome_colore && <Badge className="bg-pink-100 text-pink-800 text-[10px]">{h.nome_colore}</Badge>}
                                {h.hex_color && <span className="inline-block w-3 h-3 rounded border" style={{ background: h.hex_color }} />}
                              </div>
                              <div className="text-xs text-gray-700 line-clamp-3 group-hover:text-indigo-900">{preview}{(h.custom_prompt || h.prompt_finale || "").length > 180 ? "…" : ""}</div>
                            </button>
                          );
                        })}
                        {(data.prompt_history || []).filter(h => (h.custom_prompt || h.prompt_finale || "").trim()).length === 0 && (
                          <div className="text-xs text-gray-500 italic px-2 py-3 text-center">Nessun prompt precedente valido</div>
                        )}
                      </PopoverContent>
                    </Popover>
                  )}
                </div>
                <Textarea
                  rows={3}
                  value={customPrompt}
                  onChange={e => setCustomPrompt(e.target.value)}
                  placeholder="Es. 'Sostituisci il divano con uno moderno in pelle marrone, aggiungi un tappeto persiano'"
                  className="bg-white/80 border-indigo-200 focus-visible:ring-indigo-500 focus-visible:ring-2"
                  data-testid="da-prompt"
                />
                <p className="text-[11px] text-indigo-700/80 mt-1 italic">Descrivi come vuoi trasformare l&apos;ambiente. Sarà combinato con template + colore + note.</p>
              </div>

              <div>
                <Label>Note aggiuntive</Label>
                <Input value={extraNote} onChange={e => setExtraNote(e.target.value)} placeholder="Stile minimal, palette colori chiari..." />
              </div>

              {/* QUALITY / RESOLUTION TIER SELECTOR */}
              <div className="rounded-lg border-2 border-amber-300 bg-gradient-to-br from-amber-50 to-orange-50 p-3" data-testid="da-quality-selector">
                <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
                  <Label className="text-sm font-semibold text-amber-900 flex items-center gap-1">
                    <Sparkles className="h-4 w-4" /> Qualità / Risoluzione render
                  </Label>
                  <Button size="sm" variant="outline" onClick={handleAnalyzePhoto} disabled={!data.foto_base || analyzeLoading}
                    className="border-purple-400 text-purple-700 hover:bg-purple-50 text-xs h-7"
                    data-testid="da-analyze-btn">
                    {analyzeLoading ? <><Loader2 className="h-3 w-3 mr-1 animate-spin" /> Analisi...</> : <><Sparkles className="h-3 w-3 mr-1" /> 🔬 Analizza foto (~$0.01)</>}
                  </Button>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                  {[
                    { id: "bozza", label: "Bozza", cost: "0.03", info: "Veloce", color: "green" },
                    { id: "standard", label: "Standard", cost: "0.05", info: "Bilanciata", color: "blue" },
                    { id: "alta", label: "Alta", cost: "0.08", info: "+50% res, sharpen", color: "orange" },
                    { id: "ultra", label: "Ultra", cost: "0.15", info: "2× upscale 4K", color: "red" },
                    { id: "ultra_pro", label: "Ultra PRO", cost: "0.25", info: "Gemini 3 Pro + 2×", color: "purple" },
                  ].map(opt => {
                    const active = quality === opt.id;
                    const colorMap = {
                      green: active ? "border-emerald-600 bg-emerald-100 text-emerald-900" : "border-gray-200 hover:border-emerald-300",
                      blue: active ? "border-blue-600 bg-blue-100 text-blue-900" : "border-gray-200 hover:border-blue-300",
                      orange: active ? "border-orange-600 bg-orange-100 text-orange-900" : "border-gray-200 hover:border-orange-300",
                      red: active ? "border-red-600 bg-red-100 text-red-900" : "border-gray-200 hover:border-red-300",
                      purple: active ? "border-purple-600 bg-gradient-to-br from-purple-100 to-pink-100 text-purple-900" : "border-gray-200 hover:border-purple-300",
                    };
                    return (
                      <button
                        key={opt.id}
                        type="button"
                        onClick={() => setQuality(opt.id)}
                        className={`p-2 rounded-lg border-2 transition text-left ${colorMap[opt.color]}`}
                        data-testid={`da-quality-${opt.id}`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-semibold">{opt.label}</span>
                          {active && <Check className="h-4 w-4" />}
                        </div>
                        <div className="text-[10px] opacity-80 mt-0.5">{opt.info}</div>
                        <div className="text-[10px] font-mono opacity-70">~${opt.cost}</div>
                      </button>
                    );
                  })}
                </div>
                <p className="text-[11px] text-amber-800/80 mt-2 italic">
                  {quality === "ultra_pro" && "🟣 Ultra PRO: modello Gemini 3 Pro (native superior detail retention) + 2× upscale server-side. Massima qualità disponibile."}
                  {quality === "ultra" && "🔴 Ultra: raddoppia la risoluzione lato server (Lanczos + unsharp). Ottimo per stampa/presentazioni."}
                  {quality === "alta" && "🟠 Alta: upscale 1.5× + sharpening. Ideale per output web di qualità."}
                  {quality === "standard" && "🔵 Standard: risoluzione nativa del modello, buon compromesso qualità/costo."}
                  {quality === "bozza" && "🟢 Bozza: massima velocità, ideale per iterare sul prompt prima del render finale."}
                </p>
                <div className="mt-2 pt-2 border-t border-amber-200 flex items-center gap-2 flex-wrap">
                  <input type="checkbox" id="da-preserve-text" checked={preserveText} onChange={e => setPreserveText(e.target.checked)} data-testid="da-preserve-text" />
                  <label htmlFor="da-preserve-text" className="text-xs text-amber-900 cursor-pointer">
                    🔒 <strong>Preserva testo/loghi/insegne visibili</strong> nella foto base (raccomandato)
                  </label>
                  <div className="w-full flex items-center gap-2 mt-2">
                    <Button size="sm" variant="outline" className={maskDataUrl ? "border-emerald-500 text-emerald-700 bg-emerald-50" : "border-amber-400 text-amber-700 hover:bg-amber-50"}
                      onClick={() => setMaskDialogOpen(true)} disabled={!data.foto_base} data-testid="da-mask-open">
                      🎨 {maskDataUrl ? "Maschera preservazione ATTIVA (edit)" : "Disegna maschera preservazione al 100%"}
                    </Button>
                    {maskDataUrl && (
                      <Button size="sm" variant="ghost" onClick={() => setMaskDataUrl("")} className="text-red-600 hover:bg-red-50" data-testid="da-mask-clear">
                        <X className="h-4 w-4 mr-1" /> Rimuovi maschera
                      </Button>
                    )}
                  </div>
                  {maskDataUrl && (
                    <p className="text-[11px] text-emerald-800 italic w-full">
                      ✓ Le aree dipinte in bianco resteranno IDENTICHE alla foto base nel render finale (post-composizione lato server).
                    </p>
                  )}
                </div>
              </div>

              <Button onClick={handleGenerate} disabled={generating || !data.foto_base} className="bg-indigo-600 hover:bg-indigo-700 w-full" data-testid="da-btn-generate">
                {generating ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Generazione in corso ({quality})...</> : <><Sparkles className="h-4 w-4 mr-1" /> Genera Render con AI ({quality})</>}
              </Button>

              {/* Progress bar realistica durante generazione */}
              {generating && (
                <AiProgressBar
                  active={generating}
                  phases={[
                    { until: 8, label: "Preparazione foto base..." },
                    { until: 22, label: foto360 ? "Setup contesto 360°..." : "Costruzione prompt..." },
                    { until: 85, label: "Generazione render fotorealistico..." },
                    { until: 100, label: "Salvataggio del render..." },
                  ]}
                  testid="da-progress-generate"
                />
              )}

              {/* BATCH multi-photo → auto-tour Panorami 360° */}
              <BatchRenderPanel
                authToken={authToken}
                fotoBase={data.foto_base}
                fotoAggiuntive={data.foto_aggiuntive || []}
                promptDescrizione={data.prompt_libero || customPrompt || ""}
                foto360={foto360}
                riferimentiStile={riferimentiStile}
                quality={quality}
                preserveText={preserveText}
                preservationMask={maskDataUrl}
              />

              {/* ===== 360° PREVIEW DIALOGS ===== */}
              <Dialog open={foto360PreviewOpen} onOpenChange={setFoto360PreviewOpen}>
                <DialogContent className="max-w-4xl" data-testid="da-foto-base-360-dialog">
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2"><Globe className="h-5 w-5 text-amber-600" /> Anteprima 360° — Foto base</DialogTitle>
                    <DialogDescription>Trascina per esplorare il panorama. Zoom con la rotella.</DialogDescription>
                  </DialogHeader>
                  <div className="rounded-lg overflow-hidden bg-black" style={{ height: 520 }}>
                    {data.foto_base && (
                      <ReactPhotoSphereViewer
                        src={data.foto_base}
                        height="100%"
                        width="100%"
                        littlePlanetButton={false}
                        defaultZoomLvl={0}
                        minFov={30}
                        maxFov={110}
                        navbar={["autorotate", "zoom", "move", "fullscreen"]}
                      />
                    )}
                  </div>
                </DialogContent>
              </Dialog>

              <Dialog open={renderPreview360} onOpenChange={setRenderPreview360}>
                <DialogContent className="max-w-4xl" data-testid="da-render-360-dialog">
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2"><Globe className="h-5 w-5 text-amber-600" /> Anteprima 360° — Render generato</DialogTitle>
                    <DialogDescription>Il render AI in modalità navigabile. Puoi salvarlo come Panorama Navigabile per aprirlo dalla sezione dedicata.</DialogDescription>
                  </DialogHeader>
                  <div className="rounded-lg overflow-hidden bg-black" style={{ height: 520 }}>
                    {data.render_corrente && (
                      <ReactPhotoSphereViewer
                        src={data.render_corrente}
                        height="100%"
                        width="100%"
                        littlePlanetButton={false}
                        defaultZoomLvl={0}
                        minFov={30}
                        maxFov={110}
                        navbar={["autorotate", "zoom", "move", "fullscreen"]}
                      />
                    )}
                  </div>
                </DialogContent>
              </Dialog>

              {/* ===== GALLERY PICKER for FOTO BASE ===== */}
              <Dialog open={galleryPickerOpen} onOpenChange={(o) => !galleryPickerLoading && setGalleryPickerOpen(o)}>
                <DialogContent className="max-w-4xl max-h-[85vh] flex flex-col" data-testid="da-gallery-picker-dialog">
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2"><Sparkles className="h-5 w-5 text-indigo-600" /> Scegli foto base dalla Galleria AI</DialogTitle>
                    <DialogDescription>
                      Seleziona qualsiasi immagine (2D o 360°) dalla galleria per usarla come nuova <strong>foto base</strong>. Perfetto per iterare su render/refine già generati.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="flex items-center gap-2 border-b pb-2">
                    <Button size="sm" variant={galleryPickerFilter === "recent" ? "default" : "outline"} onClick={() => setGalleryPickerFilter("recent")}>
                      ⏱️ Ultime 10
                    </Button>
                    <Button size="sm" variant={galleryPickerFilter === "all" ? "default" : "outline"} onClick={() => setGalleryPickerFilter("all")}>
                      Tutte ({galleryPickerItems.length})
                    </Button>
                    <Button size="sm" variant={galleryPickerFilter === "2d" ? "default" : "outline"} onClick={() => setGalleryPickerFilter("2d")}>
                      2D ({galleryPickerItems.filter(g => !g.is_360).length})
                    </Button>
                    <Button size="sm" variant={galleryPickerFilter === "360" ? "default" : "outline"} onClick={() => setGalleryPickerFilter("360")}>
                      360° ({galleryPickerItems.filter(g => g.is_360).length})
                    </Button>
                  </div>
                  <div className="overflow-y-auto flex-1 py-2">
                    {galleryPickerLoading ? (
                      <div className="py-12 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-indigo-600" /></div>
                    ) : galleryPickerItems.length === 0 ? (
                      <div className="py-12 text-center text-gray-500">
                        <Sparkles className="h-10 w-10 mx-auto mb-2 text-gray-300" />
                        <p className="text-sm">La galleria è vuota.</p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                        {galleryPickerItems
                          .filter(g => galleryPickerFilter === "all" || (galleryPickerFilter === "2d" && !g.is_360) || (galleryPickerFilter === "360" && g.is_360) || galleryPickerFilter === "recent")
                          .slice(0, galleryPickerFilter === "recent" ? 10 : undefined)
                          .map(g => (
                            <button key={g.id} type="button" onClick={() => pickFromGalleryAsBase(g.id, g.is_360)}
                              className="relative rounded-lg overflow-hidden border-2 border-gray-200 hover:border-indigo-500 hover:shadow-lg transition group"
                              data-testid={`da-gallery-pick-${g.id}`}
                              title={`Usa come foto base: ${g.nome}`}>
                              <img src={g.thumbnail} alt={g.nome} className="w-full aspect-video object-cover" />
                              <div className="absolute top-1 left-1 flex gap-1">
                                {g.is_360 && <Badge className="bg-amber-100 text-amber-800 text-[9px]">360°</Badge>}
                                <Badge className="bg-indigo-100 text-indigo-800 text-[9px]">{g.source_type}</Badge>
                              </div>
                              <div className="p-1.5 text-[10px] truncate bg-white border-t" title={g.nome}>{g.nome}</div>
                              <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 bg-black/40 transition">
                                <span className="bg-indigo-600 text-white text-xs px-3 py-1.5 rounded-full font-semibold">Usa come base</span>
                              </div>
                            </button>
                          ))}
                      </div>
                    )}
                  </div>
                  <DialogFooter className="border-t pt-3">
                    <Button variant="outline" onClick={() => setGalleryPickerOpen(false)} disabled={galleryPickerLoading}>Annulla</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>

              {/* ===== ANALISI FOTO BASE (Vision) ===== */}
              <Dialog open={analyzeOpen} onOpenChange={setAnalyzeOpen}>
                <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col" data-testid="da-analyze-dialog">
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2"><Sparkles className="h-5 w-5 text-purple-600" /> Analisi Foto Base</DialogTitle>
                    <DialogDescription>
                      Gemini Vision analizza la foto base e propone rendering ottimali. Clicca un suggerimento per riempire il campo Prompt.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="overflow-y-auto flex-1 py-2 space-y-3">
                    {analyzeLoading ? (
                      <div className="py-12 flex flex-col items-center gap-2">
                        <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
                        <p className="text-sm text-gray-600">Analisi in corso...</p>
                      </div>
                    ) : analyzeResult ? (
                      <>
                        <div className="bg-slate-50 border rounded-lg p-3">
                          <div className="text-xs font-semibold text-gray-700 mb-1">📍 Tipo ambiente</div>
                          <div className="text-sm">{analyzeResult.tipo_ambiente || "—"}</div>
                          {analyzeResult.descrizione_breve && <p className="text-xs text-gray-600 mt-1 italic">{analyzeResult.descrizione_breve}</p>}
                        </div>
                        {(analyzeResult.testi_loghi_rilevati || []).length > 0 && (
                          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                            <div className="text-xs font-semibold text-amber-900 mb-1">🔒 Testi/Loghi rilevati (verranno preservati)</div>
                            <div className="flex flex-wrap gap-1">
                              {(analyzeResult.testi_loghi_rilevati || []).map((t, i) => (
                                <Badge key={i} className="bg-amber-100 text-amber-900 border border-amber-300 text-xs">{t}</Badge>
                              ))}
                            </div>
                          </div>
                        )}
                        {(analyzeResult.palette_dominante || []).length > 0 && (
                          <div className="bg-slate-50 border rounded-lg p-3">
                            <div className="text-xs font-semibold text-gray-700 mb-1">🎨 Palette dominante</div>
                            <div className="flex gap-2">
                              {(analyzeResult.palette_dominante || []).map((c, i) => (
                                <button key={i} type="button" onClick={() => { setHexColor(c); toast.success(`Colore ${c} impostato`); }}
                                  className="w-10 h-10 rounded border-2 border-white shadow" style={{ background: c }} title={`Clicca per usare ${c}`} />
                              ))}
                            </div>
                          </div>
                        )}
                        {(analyzeResult.suggerimenti_render || []).length > 0 && (
                          <div>
                            <div className="text-xs font-semibold text-gray-700 mb-2">💡 Proposte di rendering (clicca &quot;Applica proposta&quot; per prompt+template+colore)</div>
                            <div className="space-y-2">
                              {(analyzeResult.suggerimenti_render || []).map((s, i) => (
                                <div
                                  key={i}
                                  className="border-2 border-gray-200 hover:border-purple-500 hover:bg-purple-50 rounded-lg p-3 transition"
                                  data-testid={`da-analyze-suggest-${i}`}
                                >
                                  <div className="text-sm font-semibold text-purple-900 mb-1">✨ {s.titolo}</div>
                                  <div className="text-xs text-gray-700 line-clamp-3 mb-2">{s.prompt}</div>
                                  <div className="flex flex-wrap gap-1 items-center mb-2">
                                    {s.template_suggerito && <Badge variant="secondary" className="text-[10px]">🏷️ {s.template_suggerito}</Badge>}
                                    {s.target_area_suggerita && <Badge variant="secondary" className="text-[10px]">🎯 {s.target_area_suggerita}</Badge>}
                                    {s.hex_color_suggerito && (
                                      <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 bg-gray-100 rounded border">
                                        <span className="inline-block w-3 h-3 rounded border" style={{ background: s.hex_color_suggerito }} />
                                        {s.nome_colore_suggerito || s.hex_color_suggerito}
                                      </span>
                                    )}
                                  </div>
                                  <div className="flex gap-1">
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="text-xs h-7 flex-1"
                                      onClick={() => { setCustomPrompt(s.prompt || ""); setAnalyzeOpen(false); toast.success(`Solo prompt di "${s.titolo}" caricato`); }}
                                      data-testid={`da-analyze-prompt-only-${i}`}
                                    >
                                      Solo prompt
                                    </Button>
                                    <Button
                                      size="sm"
                                      className="text-xs h-7 flex-1 bg-purple-600 hover:bg-purple-700"
                                      onClick={() => {
                                        setCustomPrompt(s.prompt || "");
                                        // Try to match template by id or by fuzzy label
                                        if (s.template_suggerito) {
                                          const tid = String(s.template_suggerito).toLowerCase().trim();
                                          const match = templates.find(t => t.id === tid || (t.label || "").toLowerCase().includes(tid));
                                          if (match) setSelectedTemplate(match.id);
                                        }
                                        if (s.hex_color_suggerito) setHexColor(s.hex_color_suggerito);
                                        if (s.nome_colore_suggerito) setNomeColore(s.nome_colore_suggerito);
                                        if (s.target_area_suggerita) setTargetArea(s.target_area_suggerita);
                                        setAnalyzeOpen(false);
                                        toast.success(`🎯 "${s.titolo}" applicato completo (prompt+template+colore+area)`);
                                      }}
                                      data-testid={`da-analyze-apply-full-${i}`}
                                    >
                                      🎯 Applica proposta
                                    </Button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </>
                    ) : (
                      <p className="text-sm text-gray-500 text-center py-8">Nessun risultato disponibile.</p>
                    )}
                  </div>
                  <DialogFooter className="border-t pt-3">
                    <Button variant="outline" onClick={() => setAnalyzeOpen(false)}>Chiudi</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>

              {/* ===== MASCHERA DI PRESERVAZIONE ===== */}
              <Dialog open={maskDialogOpen} onOpenChange={setMaskDialogOpen}>
                <DialogContent className="max-w-3xl" data-testid="da-mask-dialog">
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">🎨 Maschera Preservazione</DialogTitle>
                    <DialogDescription>
                      Dipingi in BIANCO le aree della foto che vuoi <strong>preservare al 100%</strong> nel render (testo, loghi, insegne, dettagli critici). Il resto verrà modificato dall&apos;AI. La maschera viene composta lato server sul render finale.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      <label className="text-xs">Pennello: <strong>{maskBrushSize}px</strong></label>
                      <input type="range" min="8" max="80" value={maskBrushSize} onChange={e => setMaskBrushSize(Number(e.target.value))} className="flex-1 max-w-[200px]" />
                      <Button size="sm" variant="outline" onClick={() => { maskDrawingRef.current.mode = "add"; }} data-testid="da-mask-mode-add">Aggiungi ✏️</Button>
                      <Button size="sm" variant="outline" onClick={() => { maskDrawingRef.current.mode = "erase"; }} data-testid="da-mask-mode-erase">Cancella 🧽</Button>
                      <Button size="sm" variant="ghost" className="text-red-600" onClick={() => {
                        const c = maskCanvasRef.current;
                        if (c) { const ctx = c.getContext("2d"); ctx.clearRect(0, 0, c.width, c.height); }
                      }} data-testid="da-mask-reset">Reset</Button>
                    </div>
                    <div className="relative border rounded-lg overflow-hidden bg-black" style={{ height: 480 }}>
                      {data.foto_base && (
                        <>
                          <img
                            ref={maskImgRef}
                            src={data.foto_base}
                            alt="base"
                            className="absolute inset-0 w-full h-full object-contain opacity-70 pointer-events-none"
                            onLoad={(e) => {
                              const img = e.currentTarget;
                              const c = maskCanvasRef.current;
                              if (c && img.naturalWidth) {
                                c.width = img.naturalWidth;
                                c.height = img.naturalHeight;
                              }
                            }}
                          />
                          <canvas
                            ref={maskCanvasRef}
                            className="absolute inset-0 w-full h-full object-contain cursor-crosshair"
                            style={{ mixBlendMode: "screen" }}
                            data-testid="da-mask-canvas"
                            onMouseDown={(e) => {
                              maskDrawingRef.current.drawing = true;
                              const c = maskCanvasRef.current;
                              const r = c.getBoundingClientRect();
                              const x = ((e.clientX - r.left) / r.width) * c.width;
                              const y = ((e.clientY - r.top) / r.height) * c.height;
                              const ctx = c.getContext("2d");
                              ctx.globalCompositeOperation = maskDrawingRef.current.mode === "erase" ? "destination-out" : "source-over";
                              ctx.fillStyle = "#FFFFFF";
                              ctx.beginPath();
                              ctx.arc(x, y, maskBrushSize * (c.width / r.width), 0, Math.PI * 2);
                              ctx.fill();
                            }}
                            onMouseMove={(e) => {
                              if (!maskDrawingRef.current.drawing) return;
                              const c = maskCanvasRef.current;
                              const r = c.getBoundingClientRect();
                              const x = ((e.clientX - r.left) / r.width) * c.width;
                              const y = ((e.clientY - r.top) / r.height) * c.height;
                              const ctx = c.getContext("2d");
                              ctx.globalCompositeOperation = maskDrawingRef.current.mode === "erase" ? "destination-out" : "source-over";
                              ctx.fillStyle = "#FFFFFF";
                              ctx.beginPath();
                              ctx.arc(x, y, maskBrushSize * (c.width / r.width), 0, Math.PI * 2);
                              ctx.fill();
                            }}
                            onMouseUp={() => { maskDrawingRef.current.drawing = false; }}
                            onMouseLeave={() => { maskDrawingRef.current.drawing = false; }}
                          />
                        </>
                      )}
                    </div>
                    <p className="text-[11px] text-gray-500 italic">Modalità Aggiungi ✏️: pennello bianco. Modalità Cancella 🧽: rimuove dalla maschera. Il fondo trasparente = area modificabile dall&apos;AI.</p>
                  </div>
                  <DialogFooter className="border-t pt-3">
                    <Button variant="outline" onClick={() => setMaskDialogOpen(false)}>Annulla</Button>
                    <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={() => {
                      const c = maskCanvasRef.current;
                      if (!c) return;
                      // Composite canvas onto a black background to make a proper BW mask
                      const tmp = document.createElement("canvas");
                      tmp.width = c.width; tmp.height = c.height;
                      const tctx = tmp.getContext("2d");
                      tctx.fillStyle = "#000000";
                      tctx.fillRect(0, 0, tmp.width, tmp.height);
                      tctx.drawImage(c, 0, 0);
                      const dataUrl = tmp.toDataURL("image/png");
                      setMaskDataUrl(dataUrl);
                      setMaskDialogOpen(false);
                      toast.success("Maschera preservazione salvata — verrà applicata al prossimo render");
                    }} data-testid="da-mask-save">
                      <Check className="h-4 w-4 mr-1" /> Salva maschera
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>

              {/* Cost meter */}
              <div className="flex items-center justify-between gap-3 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-xs" data-testid="da-cost-meter">
                <div className="flex items-center gap-2 text-amber-900">
                  <Coins className="h-4 w-4 text-amber-600" />
                  <span>Costo stimato prossimo render: <strong className="font-mono">~${estimatedCost.toFixed(3)}</strong></span>
                  <span className="text-amber-700/70 hidden sm:inline">
                    (base $0.04
                    {(data.foto_aggiuntive || []).length > 0 && ` + ${(data.foto_aggiuntive || []).length} contesto`}
                    {riferimentiStile.length > 0 && ` + ${riferimentiStile.length} riferim.`}
                    {foto360 && ` + 360°`}
                    )
                  </span>
                </div>
                {sessionRenders > 0 && (
                  <div className="text-amber-900 whitespace-nowrap">
                    Sessione: <strong className="font-mono">{sessionRenders}</strong> render · <strong className="font-mono">~${sessionCost.toFixed(3)}</strong>
                  </div>
                )}
              </div>

              {/* Anteprima prompt finale */}
              {finalPromptPreview && (
                <div className="border border-indigo-200 rounded-lg bg-indigo-50/60" data-testid="da-prompt-preview">
                  <button type="button" onClick={() => setPreviewOpen(o => !o)} className="w-full flex items-center justify-between px-3 py-2 text-sm font-medium text-indigo-900 hover:bg-indigo-100 rounded-t-lg">
                    <span className="flex items-center gap-2"><FileText className="h-4 w-4" /> Anteprima prompt finale</span>
                    {previewOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  </button>
                  {previewOpen && (
                    <div className="px-3 pb-3 pt-1 border-t border-indigo-100">
                      <pre className="text-xs whitespace-pre-wrap font-mono text-indigo-900 bg-white p-3 rounded border border-indigo-100 max-h-60 overflow-y-auto">{finalPromptPreview}</pre>
                      <div className="text-xs text-indigo-700 mt-1 italic">Questo è il prompt esatto che verrà inviato all&apos;AI quando clicchi &quot;Genera Render&quot;.</div>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {data.render_corrente && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Render Generato</CardTitle>
                <CardDescription>Prompt usato: <em>{data.prompt_usato?.substring(0, 200)}{data.prompt_usato?.length > 200 ? "..." : ""}</em></CardDescription>
              </CardHeader>
              <CardContent>
                <div className="relative">
                  <img src={data.render_corrente} alt="render" className="max-h-96 rounded-lg border w-full object-contain bg-gray-50" />
                  {data.foto_360 && (
                    <button
                      type="button"
                      onClick={() => setRenderPreview360(true)}
                      className="absolute inset-0 flex items-center justify-center bg-black/0 hover:bg-black/40 transition group rounded-lg"
                      title="Anteprima navigabile 360° del render"
                      data-testid="da-render-play-360"
                    >
                      <span className="opacity-0 group-hover:opacity-100 bg-amber-500/95 text-white rounded-full p-3 shadow-xl transition">
                        <Play className="h-8 w-8" fill="white" />
                      </span>
                      <Badge className="absolute top-2 right-2 bg-amber-500 text-white border-0 shadow">
                        <Globe className="h-3 w-3 mr-1" /> 360°
                      </Badge>
                    </button>
                  )}
                </div>
                <div className="flex gap-2 mt-3 flex-wrap">
                  <a href={data.render_corrente} download={`render_${data.nome}.png`} className="inline-flex items-center gap-1 text-sm text-indigo-600 hover:underline">
                    <Download className="h-4 w-4" /> Scarica render
                  </a>
                  <Button size="sm" variant="outline" onClick={() => setActiveTab("oggetti")}>
                    <Package className="h-4 w-4 mr-1" /> Estrai oggetti
                  </Button>
                  {data.foto_360 && (
                    <Button size="sm" variant="outline" onClick={() => setRenderPreview360(true)} className="border-amber-400 text-amber-700 hover:bg-amber-50" data-testid="da-render-play-btn">
                      <Play className="h-4 w-4 mr-1" /> Anteprima 360°
                    </Button>
                  )}
                  {data.foto_360 && (
                    <Button size="sm" variant="outline" onClick={handleSaveAsPanorama} disabled={savingAsPano}
                      className="border-indigo-300 text-indigo-700 hover:bg-indigo-50"
                      data-testid="da-save-as-panorama">
                      {savingAsPano ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Salvataggio...</> : <><Sparkles className="h-4 w-4 mr-1" /> Salva come Panorama Navigabile 360°</>}
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Cronologia prompt */}
          {(data.prompt_history || []).length > 0 && (
            <Card data-testid="da-history-card">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2"><History className="h-4 w-4" /> Cronologia Prompt ({(data.prompt_history || []).length})</CardTitle>
                <CardDescription>Riusa un prompt precedente, modificalo e rigenera per ottenere varianti.</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {(data.prompt_history || []).map((h) => (
                    <div key={h.id} className="border border-gray-200 rounded-lg p-3 hover:bg-gray-50" data-testid={`da-history-entry-${h.id}`}>
                      <div className="flex items-center justify-between flex-wrap gap-2 mb-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge variant="secondary" className="text-xs">{new Date(h.timestamp).toLocaleString("it-IT")}</Badge>
                          {h.template_id && <Badge className="bg-indigo-100 text-indigo-800 text-xs">{h.template_id}</Badge>}
                          {h.nome_colore && <Badge className="bg-pink-100 text-pink-800 text-xs">{h.nome_colore}</Badge>}
                          {h.hex_color && <span className="inline-flex items-center gap-1 text-xs"><span className="w-3 h-3 rounded border" style={{ background: h.hex_color }}></span>{h.hex_color}</span>}
                          {h.target_area && <Badge variant="outline" className="text-xs">area: {h.target_area}</Badge>}
                        </div>
                        <div className="flex gap-1">
                          <Button size="sm" variant="outline" onClick={() => applyHistoryEntry(h)} data-testid={`da-history-reuse-${h.id}`}>
                            <RotateCcw className="h-3.5 w-3.5 mr-1" /> Riusa e modifica
                          </Button>
                          <Button size="sm" variant="ghost" className="text-red-600" onClick={() => handleDeleteHistory(h.id)}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </div>
                      <details className="text-xs text-gray-700">
                        <summary className="cursor-pointer text-indigo-600 hover:underline">Mostra prompt completo</summary>
                        <pre className="mt-1 whitespace-pre-wrap font-mono bg-gray-50 p-2 rounded border">{h.prompt_finale}</pre>
                      </details>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* SIDE-BY-SIDE */}
        <TabsContent value="sidebyside" className="mt-4">
          <Card>
            <CardHeader><CardTitle>Prima / Dopo</CardTitle></CardHeader>
            <CardContent>
              {!data.foto_base ? (
                <p className="text-gray-500 text-center py-10">Carica una foto base nella tab &quot;Genera Render&quot;.</p>
              ) : !data.render_corrente ? (
                <p className="text-gray-500 text-center py-10">Genera il render per vedere il confronto.</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <div className="text-center text-sm font-medium text-gray-700 mb-2">PRIMA</div>
                    <img src={data.foto_base} alt="prima" className="w-full rounded-lg border" />
                  </div>
                  <div>
                    <div className="text-center text-sm font-medium text-indigo-700 mb-2">DOPO</div>
                    <img src={data.render_corrente} alt="dopo" className="w-full rounded-lg border-2 border-indigo-500" />
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* INVENTARIO PROGETTO */}
        <TabsContent value="oggetti" className="space-y-4 mt-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <CardTitle className="text-base">Inventario Progetto ({(data.oggetti || []).length})</CardTitle>
                <div className="flex gap-2 flex-wrap">
                  <Button onClick={openSaveListDialog} disabled={!(data.oggetti || []).length} variant="outline" data-testid="da-btn-save-list" className="border-indigo-300 text-indigo-700 hover:bg-indigo-50">
                    <Save className="h-4 w-4 mr-1" /> Salva come lista
                  </Button>
                  <Button onClick={handleResyncRender} disabled={resyncInProgress || !(data.oggetti || []).length || !data.render_corrente} variant="outline" data-testid="da-btn-resync">
                    {resyncInProgress ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Rigenerazione...</> : <><RefreshCw className="h-4 w-4 mr-1" /> Rigenera render coerente</>}
                  </Button>
                  <Button onClick={handleAnalyzeObjects} disabled={analyzingObj || (!data.render_corrente && !analysisImage)} className="bg-indigo-600 hover:bg-indigo-700" data-testid="da-btn-analyze">
                    {analyzingObj ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Analisi in corso...</> : <><Sparkles className="h-4 w-4 mr-1" /> Analizza con AI</>}
                  </Button>
                </div>
              </div>
              <CardDescription>Carica una foto reale dell&apos;ambiente OPPURE usa il render generato; l&apos;AI proporrà l&apos;elenco oggetti.</CardDescription>
            </CardHeader>
            <CardContent>
              {/* Progress bar realistica durante analisi/rigenerazione */}
              {(analyzingObj || resyncInProgress) && (
                <div className="mb-4">
                  <AiProgressBar
                    active={analyzingObj || resyncInProgress}
                    phases={analyzingObj ? [
                      { until: 12, label: "Preparazione immagine..." },
                      { until: 30, label: "Invio al modello vision..." },
                      { until: 85, label: "Analisi oggetti in corso..." },
                      { until: 100, label: "Formattazione risultati..." },
                    ] : [
                      { until: 10, label: "Lettura inventario..." },
                      { until: 28, label: "Costruzione prompt coerente..." },
                      { until: 80, label: "Generazione render con AI..." },
                      { until: 100, label: "Salvataggio..." },
                    ]}
                    testid="da-progress"
                  />
                </div>
              )}
              {/* Upload foto per analisi diretta */}
              <div className="mb-4 p-3 rounded-lg border-2 border-dashed border-indigo-200 bg-indigo-50/40">
                <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
                  <div className="flex items-center gap-2 text-sm font-medium text-indigo-800">
                    <ImageIcon className="h-4 w-4" /> Foto per analisi inventario {analysisImage && <Badge variant="secondary" className="text-[10px]">Pronta</Badge>}
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <Button size="sm" variant="outline" onClick={() => analysisFileRef.current?.click()} data-testid="da-btn-upload-analysis">
                      <Upload className="h-4 w-4 mr-1" /> {analysisImage ? "Sostituisci" : "Carica foto"}
                    </Button>
                    <Button size="sm" variant="outline" onClick={handlePasteAnalysisImage} data-testid="da-btn-paste-analysis">
                      <Clipboard className="h-4 w-4 mr-1" /> Incolla
                    </Button>
                    {data.render_corrente && !analysisImage && (
                      <Button size="sm" variant="outline" onClick={() => setAnalysisImage({ content_base64: data.render_corrente, nome_file: "render_corrente", from_render: true })} data-testid="da-btn-use-render">
                        <Wand2 className="h-4 w-4 mr-1" /> Usa render
                      </Button>
                    )}
                    {analysisImage && (
                      <Button size="sm" variant="ghost" onClick={() => setAnalysisImage(null)} data-testid="da-btn-clear-analysis">
                        <X className="h-4 w-4 mr-1" /> Rimuovi
                      </Button>
                    )}
                  </div>
                </div>
                <input ref={analysisFileRef} type="file" accept="image/*" className="hidden" onChange={(e) => handleUploadAnalysisImage(e.target.files?.[0])} />
                {analysisImage ? (
                  <div className="flex items-start gap-3">
                    <img src={analysisImage.content_base64} alt="da analizzare" className="max-h-48 rounded-lg border border-indigo-300" data-testid="da-analysis-preview" />
                    <div className="text-xs text-gray-600 space-y-1">
                      <div><strong>File:</strong> {analysisImage.nome_file}</div>
                      <div className="text-indigo-700">Clicca <strong>&quot;Analizza con AI&quot;</strong> a destra per identificare gli oggetti e stimarne il prezzo.</div>
                      <div className="text-amber-700">Costo: ~0.01 Crediti (vision).</div>
                    </div>
                  </div>
                ) : (
                  <p className="text-xs text-gray-600">
                    {data.render_corrente
                      ? "Suggerimento: clicca \"Usa render\" per analizzare il render generato, oppure carica una foto reale (la foto verrà inviata SOLO a questa analisi, non sostituisce la foto base del progetto)."
                      : "Carica una foto reale dell'ambiente (la foto verrà usata SOLO per questa analisi, non sostituisce nulla nel progetto). Oppure genera prima un render nella tab \"Genera Render\"."
                    }
                  </p>
                )}
              </div>

              {proposals && (
                <div className="border-2 border-indigo-200 rounded-lg p-3 mb-4 bg-indigo-50">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-sm font-semibold text-indigo-800">Proposte AI ({proposals.filter(p => p._selected).length} selezionate)</h4>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => setProposals(null)}>Annulla</Button>
                      <Button size="sm" onClick={handleSaveProposals} className="bg-indigo-600 hover:bg-indigo-700" data-testid="da-btn-save-proposals"><Save className="h-4 w-4 mr-1" /> Aggiungi al progetto</Button>
                    </div>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead><tr className="text-left text-xs"><th>✓</th><th>Descrizione</th><th>Categoria</th><th>Qta</th><th>Prezzo €</th><th>Fornitore</th><th></th></tr></thead>
                      <tbody>
                        {proposals.map((p, i) => (
                          <tr key={i} className="border-t border-indigo-100">
                            <td><Checkbox checked={p._selected} onCheckedChange={() => toggleProposal(i)} /></td>
                            <td><Input value={p.descrizione} onChange={e => updateProposal(i, "descrizione", e.target.value)} className="text-xs h-8" /></td>
                            <td><Input value={p.categoria} onChange={e => updateProposal(i, "categoria", e.target.value)} className="text-xs h-8 w-24" /></td>
                            <td><Input type="number" value={p.quantita} onChange={e => updateProposal(i, "quantita", e.target.value)} className="text-xs h-8 w-16" /></td>
                            <td><Input type="number" step="0.01" value={p.prezzo_stimato} onChange={e => updateProposal(i, "prezzo_stimato", e.target.value)} className="text-xs h-8 w-24" /></td>
                            <td><Input value={p.fornitore} onChange={e => updateProposal(i, "fornitore", e.target.value)} className="text-xs h-8 w-28" /></td>
                            <td><Button size="sm" variant="ghost" onClick={() => removeProposal(i)}><X className="h-3 w-3" /></Button></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {(data.oggetti || []).length === 0 ? (
                <p className="text-center text-gray-500 py-10">Nessun oggetto salvato. Genera un render e usa &quot;Analizza con AI&quot;.</p>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 text-left">
                        <tr>
                          <th className="px-2 py-2">Descrizione</th>
                          <th className="px-2 py-2">Categoria</th>
                          <th className="px-2 py-2">Qta</th>
                          <th className="px-2 py-2">€ unit.</th>
                          <th className="px-2 py-2">Totale</th>
                          <th className="px-2 py-2">Fornitore</th>
                          <th className="px-2 py-2 text-right">Azioni</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(data.oggetti || []).map((o, i) => {
                          const tot = (parseFloat(o.prezzo_stimato) || 0) * (parseInt(o.quantita) || 1);
                          return (
                            <tr key={i} className="border-t hover:bg-gray-50">
                              <td className="px-2 py-2">{o.descrizione}</td>
                              <td className="px-2 py-2"><Badge variant="secondary">{o.categoria || "—"}</Badge></td>
                              <td className="px-2 py-2">{o.quantita}</td>
                              <td className="px-2 py-2">€ {(o.prezzo_stimato || 0).toFixed(2)}</td>
                              <td className="px-2 py-2 font-semibold">€ {tot.toFixed(2)}</td>
                              <td className="px-2 py-2 text-xs text-gray-600">{o.fornitore}</td>
                              <td className="px-2 py-2 text-right">
                                <Button size="sm" variant="ghost" className="text-red-600" onClick={() => removeOggetto(i)}><Trash2 className="h-4 w-4" /></Button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                      <tfoot>
                        <tr className="border-t-2 border-indigo-300 bg-indigo-50">
                          <td colSpan="4" className="px-2 py-2 font-bold text-right">TOTALE STIMA OGGETTI</td>
                          <td className="px-2 py-2 font-bold text-indigo-800 text-lg">€ {totaleOggetti.toFixed(2)}</td>
                          <td colSpan="2"></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Named saved lists (Blocco B, Feb 2026) */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <ListChecks className="h-4 w-4 text-indigo-600" /> Liste Inventario salvate ({inventoryLists.length})
              </CardTitle>
              <CardDescription>Salva istantanee dell&apos;inventario per riusarle nel Computo o esportarle in PDF/Excel.</CardDescription>
            </CardHeader>
            <CardContent>
              {inventoryLists.length === 0 ? (
                <p className="text-center text-gray-500 py-6 text-sm">
                  Nessuna lista salvata. Aggiungi oggetti sopra e clicca <strong>&quot;Salva come lista&quot;</strong>.
                </p>
              ) : (
                <div className="space-y-2">
                  {inventoryLists.map(lst => {
                    const cnt = (lst.oggetti || []).length;
                    const totale = (lst.oggetti || []).reduce((s, o) => s + (parseFloat(o.prezzo_stimato) || 0) * (parseInt(o.quantita) || 1), 0);
                    return (
                      <Card key={lst.id} className="border-l-4 border-l-emerald-500" data-testid={`da-inv-list-${lst.id}`}>
                        <CardContent className="p-3 flex items-center justify-between flex-wrap gap-2">
                          <div className="flex-1 min-w-[220px]">
                            <div className="font-semibold">{lst.nome}</div>
                            <div className="text-xs text-gray-500">{cnt} oggetti • Totale € {totale.toFixed(2)} • {new Date(lst.updated_at || lst.created_at).toLocaleString("it-IT")}</div>
                            {lst.descrizione && <div className="text-xs text-gray-600 mt-1">{lst.descrizione}</div>}
                          </div>
                          <div className="flex gap-1 flex-wrap">
                            <Button size="sm" variant="outline" onClick={() => loadListToCurrent(lst)} title="Carica come inventario" data-testid={`da-inv-load-${lst.id}`}>
                              <Upload className="h-3.5 w-3.5 mr-1" /> Carica
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => downloadPdf(`${API}/design/inventory-lists/${lst.id}/pdf?token=${authToken}`, `inventario_${lst.nome}.pdf`)} title="PDF" data-testid={`da-inv-pdf-${lst.id}`}>
                              <Download className="h-4 w-4" />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => downloadXlsx(`${API}/design/inventory-lists/${lst.id}/excel?token=${authToken}`, `inventario_${lst.nome}.xlsx`)} title="Excel" data-testid={`da-inv-xlsx-${lst.id}`} className="text-emerald-700">
                              <FileText className="h-4 w-4" />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => openUpdateListDialog(lst)} title="Rinomina/aggiorna"><Pencil className="h-4 w-4" /></Button>
                            <Button size="sm" variant="ghost" className="text-red-600" onClick={() => deleteInventoryList(lst)} title="Elimina" data-testid={`da-inv-del-${lst.id}`}><Trash2 className="h-4 w-4" /></Button>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* COMPUTO METRICO */}
        <TabsContent value="computo" className="space-y-4 mt-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <CardTitle className="text-base">Computi Metrici di questo Progetto ({computi.length})</CardTitle>
                <Button onClick={openNewComputo} className="bg-indigo-600 hover:bg-indigo-700" data-testid="da-btn-new-computo"><Plus className="h-4 w-4 mr-1" /> Nuovo Computo</Button>
              </div>
            </CardHeader>
            <CardContent>
              {computi.length === 0 ? (
                <p className="text-center text-gray-500 py-10">Nessun computo. Crea il primo computo per stimare i lavori.</p>
              ) : (
                <div className="space-y-2">
                  {computi.map(c => {
                    const tot = (c.righe || []).reduce((s, r) => s + (parseFloat(r.quantita) || 0) * (parseFloat(r.prezzo_unitario) || 0), 0);
                    return (
                      <Card key={c.id} className="border-l-4 border-l-indigo-500">
                        <CardContent className="p-3 flex items-center justify-between">
                          <div>
                            <div className="font-semibold">{c.nome}</div>
                            <div className="text-xs text-gray-500">{(c.righe || []).length} voci • Totale € {tot.toFixed(2)}</div>
                          </div>
                          <div className="flex gap-1">
                            <Button size="sm" variant="ghost" onClick={() => downloadPdf(`${API}/computo/computi/${c.id}/pdf?token=${authToken}`, `computo_${c.nome}.pdf`)} title="PDF"><Download className="h-4 w-4" /></Button>
                            <Button size="sm" variant="ghost" onClick={() => downloadXlsx(`${API}/computo/computi/${c.id}/excel?token=${authToken}`, `computo_${c.nome}.xlsx`)} title="Excel" className="text-emerald-700" data-testid={`da-comp-xlsx-${c.id}`}><FileText className="h-4 w-4" /></Button>
                            <Button size="sm" variant="ghost" onClick={() => openEditComputo(c)}><Pencil className="h-4 w-4" /></Button>
                            <Button size="sm" variant="ghost" className="text-red-600" onClick={() => handleDeleteComputo(c.id)}><Trash2 className="h-4 w-4" /></Button>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <ComputoDialog
        open={computoDialogOpen}
        onOpenChange={setComputoDialogOpen}
        computo={editingComputo}
        voci={voci}
        onSave={handleSaveComputo}
        authToken={authToken}
        progettoId={progetto.id}
      />
      <TemplateManagerDialog
        open={tplDialogOpen}
        onOpenChange={setTplDialogOpen}
        templates={templates}
        onChanged={loadTemplates}
        authToken={authToken}
      />

      {/* Dialog conferma rimozione oggetto */}
      <Dialog open={removeObjDialog.open} onOpenChange={(o) => setRemoveObjDialog(prev => ({ ...prev, open: o }))}>
        <DialogContent data-testid="da-remove-obj-dialog">
          <DialogHeader>
            <DialogTitle>Rimuovi oggetto</DialogTitle>
            <DialogDescription>
              Stai eliminando <strong>{removeObjDialog.oggetto?.descrizione || ""}</strong> dall&apos;inventario del progetto.
              Vuoi che venga rimosso anche dal render? (richiede rigenerazione AI, ~1 credito)
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 flex-wrap">
            <Button variant="outline" onClick={() => setRemoveObjDialog({ open: false, oggetto: null, idx: -1 })}>Annulla</Button>
            <Button variant="outline" onClick={() => confirmRemoveOggetto(false)} data-testid="da-remove-only-inv">
              <Trash2 className="h-4 w-4 mr-1" /> Solo dall&apos;inventario
            </Button>
            <Button onClick={() => confirmRemoveOggetto(true)} className="bg-indigo-600 hover:bg-indigo-700" data-testid="da-remove-also-render">
              <Wand2 className="h-4 w-4 mr-1" /> Anche dal render
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Save Inventory List dialog (Blocco B) */}
      <Dialog open={saveListDialogOpen} onOpenChange={setSaveListDialogOpen}>
        <DialogContent data-testid="da-save-list-dialog">
          <DialogHeader>
            <DialogTitle>{saveListForm.editing_id ? "Aggiorna lista inventario" : "Salva come lista inventario"}</DialogTitle>
            <DialogDescription>
              {saveListForm.editing_id
                ? "Modifica nome/descrizione della lista salvata."
                : `Salva gli attuali ${(data.oggetti || []).length} oggetti come lista riutilizzabile (importabile nei Computi, esportabile in PDF/Excel).`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Nome lista *</Label>
              <Input
                value={saveListForm.nome}
                onChange={e => setSaveListForm(p => ({ ...p, nome: e.target.value }))}
                placeholder="Es. Inventario ristorante v1"
                data-testid="da-save-list-name"
              />
            </div>
            <div>
              <Label>Descrizione (opz.)</Label>
              <Textarea
                rows={2}
                value={saveListForm.descrizione}
                onChange={e => setSaveListForm(p => ({ ...p, descrizione: e.target.value }))}
                placeholder="Descrizione o note per identificare la lista"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSaveListDialogOpen(false)}>Annulla</Button>
            <Button onClick={handleSaveList} className="bg-indigo-600 hover:bg-indigo-700" data-testid="da-save-list-confirm">
              <Save className="h-4 w-4 mr-1" /> {saveListForm.editing_id ? "Aggiorna" : "Salva"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};


// ====================== TEMPLATE MANAGER DIALOG ======================
const TemplateManagerDialog = ({ open, onOpenChange, templates, onChanged, authToken }) => {
  const [items, setItems] = useState([]);
  const [savingId, setSavingId] = useState(null);

  useEffect(() => {
    if (open) {
      // clone (each has _dirty flag)
      setItems((templates || []).map(t => ({ ...t, _dirty: false, _new: false })));
    }
  }, [open, templates]);

  const updateItem = (idx, field, val) => {
    setItems(prev => prev.map((it, i) => i === idx ? { ...it, [field]: val, _dirty: true } : it));
  };

  const addNew = () => {
    setItems(prev => [...prev, {
      id: `new-${Date.now()}`,
      label: "",
      prompt: "",
      icon: "Sparkles",
      _new: true,
      _dirty: true,
    }]);
  };

  const saveItem = async (idx) => {
    const it = items[idx];
    if (!it.label?.trim() || !it.prompt?.trim()) {
      toast.error("Compila label e prompt");
      return;
    }
    setSavingId(it.id);
    try {
      if (it._new) {
        const r = await axios.post(`${API}/design/templates?token=${authToken}`, {
          label: it.label, prompt: it.prompt, icon: it.icon || "Sparkles",
        });
        setItems(prev => prev.map((x, i) => i === idx ? { ...r.data, _dirty: false, _new: false } : x));
      } else {
        const r = await axios.put(`${API}/design/templates/${it.id}?token=${authToken}`, {
          label: it.label, prompt: it.prompt, icon: it.icon || "Sparkles",
        });
        setItems(prev => prev.map((x, i) => i === idx ? { ...r.data, _dirty: false } : x));
      }
      toast.success("Template salvato");
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore salvataggio");
    } finally { setSavingId(null); }
  };

  const removeItem = async (idx) => {
    const it = items[idx];
    if (it._new) {
      setItems(prev => prev.filter((_, i) => i !== idx));
      return;
    }
    if (!window.confirm(`Eliminare il template "${it.label}"?`)) return;
    try {
      await axios.delete(`${API}/design/templates/${it.id}?token=${authToken}`);
      setItems(prev => prev.filter((_, i) => i !== idx));
      toast.success("Template eliminato");
      onChanged?.();
    } catch { toast.error("Errore"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="da-tpl-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Settings className="h-5 w-5" /> Gestisci Template</DialogTitle>
          <DialogDescription>
            Modifica i template esistenti o creane di nuovi. Il prompt è la descrizione AI che verrà inviata a Nano Banana.
          </DialogDescription>
        </DialogHeader>

        <div className="flex justify-end">
          <Button size="sm" variant="outline" onClick={addNew} data-testid="da-tpl-add"><Plus className="h-4 w-4 mr-1" /> Nuovo template</Button>
        </div>

        <div className="space-y-3">
          {items.map((it, idx) => (
            <Card key={it.id} className={`border-l-4 ${it._dirty ? "border-l-amber-400" : "border-l-indigo-400"}`} data-testid={`da-tpl-item-${idx}`}>
              <CardContent className="p-3 space-y-2">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs">Nome / Label *</Label>
                    <Input value={it.label || ""} onChange={e => updateItem(idx, "label", e.target.value)} placeholder="Es. Spa & Benessere" />
                  </div>
                  <div>
                    <Label className="text-xs">Icona (lucide-react)</Label>
                    <Input value={it.icon || ""} onChange={e => updateItem(idx, "icon", e.target.value)} placeholder="Sparkles, Utensils, Briefcase..." />
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Prompt AI *</Label>
                  <Textarea rows={4} value={it.prompt || ""} onChange={e => updateItem(idx, "prompt", e.target.value)}
                    placeholder="Descrivi come trasformare l'ambiente. Es. 'Trasforma in un ambiente moderno con...'" />
                </div>
                <div className="flex justify-between items-center">
                  <div className="text-xs text-gray-500">
                    {it._new ? <Badge variant="secondary">Nuovo</Badge> : <span className="font-mono">{it.id}</span>}
                    {it._dirty && <Badge className="ml-2 bg-amber-100 text-amber-800">Modificato</Badge>}
                  </div>
                  <div className="flex gap-1">
                    <Button size="sm" variant="ghost" className="text-red-600" onClick={() => removeItem(idx)} data-testid={`da-tpl-del-${idx}`}><Trash2 className="h-4 w-4" /></Button>
                    <Button size="sm" disabled={!it._dirty || savingId === it.id} onClick={() => saveItem(idx)} className="bg-indigo-600 hover:bg-indigo-700" data-testid={`da-tpl-save-${idx}`}>
                      {savingId === it.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1" /> Salva</>}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
          {items.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              <ListChecks className="h-10 w-10 mx-auto opacity-30 mb-2" />
              <p>Nessun template. Clicca &quot;Nuovo template&quot; per crearne uno.</p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Chiudi</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};


// ====================== COMPUTO DIALOG (riusabile) ======================
const ComputoDialog = ({ open, onOpenChange, computo, voci, onSave, authToken, progettoId }) => {
  const [form, setForm] = useState({});
  const [invPickerOpen, setInvPickerOpen] = useState(false);
  const [invLists, setInvLists] = useState([]);
  const [invLoading, setInvLoading] = useState(false);
  const [invFilterProgetto, setInvFilterProgetto] = useState(true); // default: only current progetto

  useEffect(() => {
    if (open) setForm(computo || { nome: "", descrizione: "", righe: [], note: "", progetto_id: progettoId || "" });
  }, [open, computo, progettoId]);

  const loadInvLists = async () => {
    setInvLoading(true);
    try {
      const pid = form.progetto_id || progettoId || "";
      const url = invFilterProgetto && pid
        ? `${API}/design/inventory-lists?token=${authToken}&progetto_id=${pid}`
        : `${API}/design/inventory-lists?token=${authToken}`;
      const r = await axios.get(url);
      setInvLists(r.data || []);
    } catch { toast.error("Errore caricamento liste inventario"); }
    finally { setInvLoading(false); }
  };

  const openInvPicker = async () => {
    setInvPickerOpen(true);
    await loadInvLists();
  };

  useEffect(() => {
    if (invPickerOpen) loadInvLists();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invFilterProgetto]);

  const importFromInvList = (lst) => {
    const newRighe = (lst.oggetti || []).map(o => ({
      id: `r-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      voce_id: "",
      descrizione: (o.descrizione || "").slice(0, 200),
      unita_misura: "cad",
      quantita: parseInt(o.quantita) || 1,
      prezzo_unitario: parseFloat(o.prezzo_stimato) || 0,
      note: o.fornitore ? `Fornitore: ${o.fornitore}` : "",
    }));
    setForm(prev => ({ ...prev, righe: [...(prev.righe || []), ...newRighe] }));
    setInvPickerOpen(false);
    toast.success(`Importate ${newRighe.length} righe da "${lst.nome}"`);
  };

  const setField = (k, v) => setForm(prev => ({ ...prev, [k]: v }));
  const addRiga = () => setField("righe", [...(form.righe || []), { id: `r-${Date.now()}`, descrizione: "", unita_misura: "cad", quantita: 1, prezzo_unitario: 0, note: "" }]);
  const setRigaField = (i, k, v) => setField("righe", form.righe.map((r, idx) => idx === i ? { ...r, [k]: v } : r));
  const removeRiga = (i) => setField("righe", form.righe.filter((_, idx) => idx !== i));

  const importVoceFromCatalogo = (i, voceId) => {
    const v = voci.find(x => x.id === voceId);
    if (!v) return;
    setField("righe", form.righe.map((r, idx) => idx === i ? {
      ...r,
      voce_id: v.id,
      descrizione: v.descrizione,
      unita_misura: v.unita_misura,
      prezzo_unitario: v.prezzo_unitario,
    } : r));
  };

  const totale = (form.righe || []).reduce((s, r) => s + (parseFloat(r.quantita) || 0) * (parseFloat(r.prezzo_unitario) || 0), 0);

  const handleSave = async () => {
    if (!form.nome?.trim()) { toast.error("Nome obbligatorio"); return; }
    await onSave({ ...form });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="computo-dialog">
        <DialogHeader>
          <DialogTitle>{form.id ? "Modifica Computo" : "Nuovo Computo Metrico"}</DialogTitle>
          <DialogDescription>Inserisci le voci di lavoro con quantità e prezzi unitari</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div><Label>Nome *</Label><Input value={form.nome || ""} onChange={e => setField("nome", e.target.value)} data-testid="computo-nome" /></div>
          <div><Label>Descrizione</Label><Input value={form.descrizione || ""} onChange={e => setField("descrizione", e.target.value)} /></div>
        </div>

        <Separator />

        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold">Voci di lavoro ({(form.righe || []).length})</h3>
          <div className="flex gap-2">
            <Button size="sm" type="button" variant="outline" onClick={openInvPicker} data-testid="computo-import-inv" className="border-indigo-300 text-indigo-700 hover:bg-indigo-50">
              <Package className="h-4 w-4 mr-1" /> Importa da Inventario
            </Button>
            <Button size="sm" type="button" variant="outline" onClick={addRiga} data-testid="computo-add-riga"><Plus className="h-4 w-4 mr-1" /> Aggiungi riga</Button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs">
              <tr>
                <th className="p-1">Da catalogo</th>
                <th className="p-1">Descrizione</th>
                <th className="p-1">U.M.</th>
                <th className="p-1">Qta</th>
                <th className="p-1">€ unit.</th>
                <th className="p-1">Totale</th>
                <th className="p-1"></th>
              </tr>
            </thead>
            <tbody>
              {(form.righe || []).map((r, i) => {
                const tot = (parseFloat(r.quantita) || 0) * (parseFloat(r.prezzo_unitario) || 0);
                return (
                  <tr key={r.id || i} className="border-t">
                    <td className="p-1">
                      <Select value={r.voce_id || "none"} onValueChange={(v) => v !== "none" && importVoceFromCatalogo(i, v)}>
                        <SelectTrigger className="h-8 text-xs w-44"><SelectValue placeholder="Seleziona..." /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">— Manuale —</SelectItem>
                          {(voci || []).map(v => <SelectItem key={v.id} value={v.id}>{v.codice} - {v.descrizione.substring(0, 40)}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="p-1"><Input value={r.descrizione || ""} onChange={e => setRigaField(i, "descrizione", e.target.value)} className="h-8 text-xs" /></td>
                    <td className="p-1"><Input value={r.unita_misura || ""} onChange={e => setRigaField(i, "unita_misura", e.target.value)} className="h-8 text-xs w-16" /></td>
                    <td className="p-1"><Input type="number" step="0.01" value={r.quantita} onChange={e => setRigaField(i, "quantita", e.target.value)} className="h-8 text-xs w-20" /></td>
                    <td className="p-1"><Input type="number" step="0.01" value={r.prezzo_unitario} onChange={e => setRigaField(i, "prezzo_unitario", e.target.value)} className="h-8 text-xs w-24" /></td>
                    <td className="p-1 font-medium">€ {tot.toFixed(2)}</td>
                    <td className="p-1"><Button size="sm" variant="ghost" onClick={() => removeRiga(i)}><X className="h-3 w-3" /></Button></td>
                  </tr>
                );
              })}
              {(form.righe || []).length === 0 && (
                <tr><td colSpan="7" className="text-center text-gray-500 py-6 text-xs">Nessuna riga. Clicca &quot;Aggiungi riga&quot;.</td></tr>
              )}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-indigo-300 bg-indigo-50">
                <td colSpan="5" className="p-2 font-bold text-right">TOTALE COMPUTO</td>
                <td colSpan="2" className="p-2 font-bold text-indigo-800 text-base">€ {totale.toFixed(2)}</td>
              </tr>
            </tfoot>
          </table>
        </div>

        <div>
          <Label>Note</Label>
          <Textarea rows={2} value={form.note || ""} onChange={e => setField("note", e.target.value)} />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button>
          <Button onClick={handleSave} data-testid="btn-save-computo"><Save className="h-4 w-4 mr-1" /> Salva</Button>
        </DialogFooter>
      </DialogContent>

      {/* Inventory List Picker (import as rows) */}
      <Dialog open={invPickerOpen} onOpenChange={setInvPickerOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto" data-testid="computo-inv-picker">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Package className="h-5 w-5 text-indigo-600" /> Importa da liste Inventario</DialogTitle>
            <DialogDescription>Aggiungi al computo le righe di una lista di inventario salvata. Le voci verranno importate come righe editabili.</DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-2 py-2">
            <label className="flex items-center gap-2 text-xs px-2 py-1 bg-gray-50 rounded border cursor-pointer">
              <input type="checkbox" checked={invFilterProgetto} onChange={e => setInvFilterProgetto(e.target.checked)} data-testid="computo-inv-only-progetto" />
              Solo liste di questo progetto
            </label>
            {invLoading && <Loader2 className="h-4 w-4 animate-spin text-gray-500" />}
          </div>
          <div className="space-y-2">
            {invLists.length === 0 && !invLoading ? (
              <p className="text-center py-8 text-gray-500 text-sm">Nessuna lista salvata. Vai nella tab &quot;Inventario Progetto&quot; e clicca &quot;Salva come lista&quot;.</p>
            ) : (
              invLists.map(lst => {
                const cnt = (lst.oggetti || []).length;
                const totale = (lst.oggetti || []).reduce((s, o) => s + (parseFloat(o.prezzo_stimato) || 0) * (parseInt(o.quantita) || 1), 0);
                return (
                  <Card key={lst.id} className="border-l-4 border-l-indigo-500" data-testid={`computo-inv-list-${lst.id}`}>
                    <CardContent className="p-3 flex items-center justify-between flex-wrap gap-2">
                      <div>
                        <div className="font-semibold flex items-center gap-2">
                          {lst.nome}
                          {lst.progetto_id && lst.progetto_id === (form.progetto_id || progettoId) && (
                            <Badge variant="secondary" className="text-[10px]">Questo progetto</Badge>
                          )}
                        </div>
                        <div className="text-xs text-gray-500">{cnt} oggetti • Totale € {totale.toFixed(2)}</div>
                        {lst.descrizione && <div className="text-xs text-gray-600 mt-1">{lst.descrizione}</div>}
                      </div>
                      <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700" onClick={() => importFromInvList(lst)} data-testid={`computo-inv-import-${lst.id}`}>
                        <Plus className="h-4 w-4 mr-1" /> Aggiungi al computo
                      </Button>
                    </CardContent>
                  </Card>
                );
              })
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setInvPickerOpen(false)}>Chiudi</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
};


// ====================== MAIN ======================
export default function DesignAssistant({ authToken }) {
  const [progetti, setProgetti] = useState([]);
  const [voci, setVoci] = useState([]);
  const [editing, setEditing] = useState(null);
  const [newDialogOpen, setNewDialogOpen] = useState(false);
  const [newForm, setNewForm] = useState({ nome: "", descrizione: "" });
  const [mainTab, setMainTab] = useState("progetti");
  const [galleryItems, setGalleryItems] = useState([]);
  const [galleryLoading, setGalleryLoading] = useState(false);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/design/progetti?token=${authToken}`);
      setProgetti(r.data || []);
    } catch { /* ignore */ }
  };
  const loadVoci = async () => {
    try {
      const r = await axios.get(`${API}/computo/voci?token=${authToken}`);
      setVoci(r.data || []);
    } catch { /* ignore */ }
  };
  const loadGallery = async () => {
    setGalleryLoading(true);
    try {
      const r = await axios.get(`${API}/design/gallery?token=${authToken}`);
      setGalleryItems(r.data || []);
    } catch { /* ignore */ }
    finally { setGalleryLoading(false); }
  };

  useEffect(() => {
    if (!authToken) return;
    load(); loadVoci(); loadGallery();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authToken]);

  const handleRefineGalleryItem = async (gid) => {
    try {
      const r = await axios.post(`${API}/design/gallery/${gid}/refine-into-progetto?token=${authToken}`, {});
      toast.success("Progetto creato: rifinisci l'immagine con prompt/colori");
      await load();
      setEditing(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore creazione progetto");
    }
  };

  const handleDeleteGalleryItem = async (gid) => {
    if (!window.confirm("Eliminare questa immagine dalla galleria? (i progetti collegati non verranno toccati)")) return;
    try {
      await axios.delete(`${API}/design/gallery/${gid}?token=${authToken}`);
      setGalleryItems(prev => prev.filter(g => g.id !== gid));
    } catch { toast.error("Errore eliminazione"); }
  };

  const handleCreate = async () => {
    if (!newForm.nome.trim()) { toast.error("Nome obbligatorio"); return; }
    try {
      const r = await axios.post(`${API}/design/progetti?token=${authToken}`, newForm);
      setNewDialogOpen(false);
      setNewForm({ nome: "", descrizione: "" });
      load();
      setEditing(r.data);
    } catch { toast.error("Errore"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Eliminare il progetto?")) return;
    try {
      await axios.delete(`${API}/design/progetti/${id}?token=${authToken}`);
      load();
    } catch { toast.error("Errore"); }
  };

  if (editing) {
    return <ProgettoEditor
      progetto={editing}
      onClose={() => { setEditing(null); load(); }}
      onUpdated={load}
      authToken={authToken}
      voci={voci}
    />;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2 text-indigo-700">
            <Sparkles className="h-6 w-6" /> AI Design Assistant
          </h2>
          <p className="text-sm text-gray-500">Trasforma i tuoi ambienti con AI: render fotorealistici, inventario e computo metrico.</p>
        </div>
        <Button onClick={() => setNewDialogOpen(true)} className="bg-indigo-600 hover:bg-indigo-700" data-testid="da-btn-new-progetto">
          <Plus className="h-4 w-4 mr-1" /> Nuovo Progetto
        </Button>
      </div>

      <Tabs value={mainTab} onValueChange={(v) => { setMainTab(v); if (v === "gallery") loadGallery(); }}>
        <TabsList className="bg-indigo-50">
          <TabsTrigger value="progetti" data-testid="da-main-tab-progetti" className="data-[state=active]:bg-indigo-600 data-[state=active]:text-white">
            <Layers className="h-4 w-4 mr-1" /> Progetti ({progetti.length})
          </TabsTrigger>
          <TabsTrigger value="gallery" data-testid="da-main-tab-gallery" className="data-[state=active]:bg-indigo-600 data-[state=active]:text-white">
            <ImageIcon className="h-4 w-4 mr-1" /> Galleria AI ({galleryItems.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="progetti" className="mt-4">
          {progetti.length === 0 ? (
            <Card>
              <CardContent className="py-16 text-center">
                <Sparkles className="h-16 w-16 mx-auto text-indigo-300 mb-4" />
                <h3 className="text-lg font-semibold text-gray-700">Inizia il tuo primo progetto di design</h3>
                <p className="text-sm text-gray-500 mb-4">Carica una foto, scegli un template commerciale e genera un render fotorealistico in pochi secondi.</p>
                <Button onClick={() => setNewDialogOpen(true)} className="bg-indigo-600 hover:bg-indigo-700"><Plus className="h-4 w-4 mr-1" /> Crea Progetto</Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {progetti.map(p => (
                <Card key={p.id} className="cursor-pointer hover:shadow-lg transition" data-testid={`da-progetto-${p.id}`}>
                  <CardHeader>
                    <CardTitle className="text-base">{p.nome}</CardTitle>
                    {p.descrizione && <CardDescription className="text-xs">{p.descrizione}</CardDescription>}
                  </CardHeader>
                  <CardContent>
                    <div className="text-xs text-gray-500 mb-3">
                      {p.template_usato && <Badge variant="secondary" className="mr-1">{p.template_usato}</Badge>}
                      Aggiornato {new Date(p.updated_at).toLocaleDateString("it-IT")}
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      <Button size="sm" onClick={() => setEditing(p)} className="bg-indigo-600 hover:bg-indigo-700" data-testid={`da-open-${p.id}`}><Eye className="h-4 w-4 mr-1" /> Apri</Button>
                      <Button size="sm" variant="ghost" className="text-red-600" onClick={() => handleDelete(p.id)}><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="gallery" className="mt-4">
          <GalleryPanel
            items={galleryItems}
            loading={galleryLoading}
            onRefresh={loadGallery}
            onRefine={handleRefineGalleryItem}
            onDelete={handleDeleteGalleryItem}
            authToken={authToken}
          />
        </TabsContent>
      </Tabs>

      <Dialog open={newDialogOpen} onOpenChange={setNewDialogOpen}>
        <DialogContent data-testid="da-new-dialog">
          <DialogHeader><DialogTitle>Nuovo Progetto di Design</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Nome *</Label><Input value={newForm.nome} onChange={e => setNewForm(p => ({ ...p, nome: e.target.value }))} placeholder="Es. Ristrutturazione Sala" data-testid="da-input-nome" /></div>
            <div><Label>Descrizione</Label><Textarea rows={2} value={newForm.descrizione} onChange={e => setNewForm(p => ({ ...p, descrizione: e.target.value }))} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewDialogOpen(false)}>Annulla</Button>
            <Button onClick={handleCreate} className="bg-indigo-600 hover:bg-indigo-700" data-testid="da-btn-create"><Save className="h-4 w-4 mr-1" /> Crea</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ================== GALLERIA AI ==================
function GalleryPanel({ items, loading, onRefresh, onRefine, onDelete, authToken }) {
  const [preview, setPreview] = useState(null);   // gallery item being previewed (full res)
  const [loadingFull, setLoadingFull] = useState(false);
  const [preview360, setPreview360] = useState(false);  // when true, render the 360 viewer instead of a flat img

  const openPreview = async (item, as360 = false) => {
    setLoadingFull(true);
    setPreview({ ...item, image_base64: "" });
    setPreview360(as360 && !!item.is_360);
    try {
      const r = await axios.get(`${API}/design/gallery/${item.id}?token=${authToken}`);
      setPreview(r.data);
    } catch {
      toast.error("Errore caricamento immagine");
      setPreview(null);
    } finally { setLoadingFull(false); }
  };

  const sourceLabel = (t) => {
    if (t === "batch") return { text: "Batch", color: "bg-purple-100 text-purple-700 border-purple-200" };
    if (t === "refined") return { text: "Rifinita", color: "bg-emerald-100 text-emerald-700 border-emerald-200" };
    return { text: "Render", color: "bg-indigo-100 text-indigo-700 border-indigo-200" };
  };

  if (loading) {
    return <div className="py-12 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-indigo-600" /></div>;
  }

  if (!items || items.length === 0) {
    return (
      <Card>
        <CardContent className="py-16 text-center">
          <ImageIcon className="h-16 w-16 mx-auto text-indigo-300 mb-4" />
          <h3 className="text-lg font-semibold text-gray-700">La tua galleria è vuota</h3>
          <p className="text-sm text-gray-500 mb-4">Ogni render (singolo o batch) verrà salvato qui e sarà riutilizzabile come base per ulteriori rifiniture o nuovi batch.</p>
          <Button variant="outline" onClick={onRefresh} data-testid="da-gallery-refresh"><RefreshCw className="h-4 w-4 mr-1" /> Aggiorna</Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-xs text-gray-500">Clicca un&apos;immagine per anteprima e azioni. Rifinisci = crea un nuovo Progetto con questa immagine come base.</p>
        <Button size="sm" variant="outline" onClick={onRefresh} data-testid="da-gallery-refresh"><RefreshCw className="h-4 w-4 mr-1" /> Aggiorna</Button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        {items.map(item => {
          const src = sourceLabel(item.source_type || "render");
          return (
            <Card key={item.id} className="overflow-hidden group relative hover:shadow-lg transition" data-testid={`da-gallery-item-${item.id}`}>
              <button
                type="button"
                onClick={() => openPreview(item)}
                className="block w-full aspect-square bg-slate-100 overflow-hidden"
                data-testid={`da-gallery-open-${item.id}`}
              >
                {item.thumbnail ? (
                  <img src={item.thumbnail} alt={item.nome} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-gray-400"><ImageIcon className="h-8 w-8" /></div>
                )}
              </button>
              <div className="absolute top-1 left-1 flex gap-1">
                <Badge className={`text-[10px] border ${src.color}`}>{src.text}</Badge>
                {item.is_360 && <Badge className="bg-amber-100 text-amber-700 border border-amber-200 text-[10px]">360°</Badge>}
              </div>
              <button
                type="button"
                onClick={() => onDelete(item.id)}
                className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                title="Elimina"
                data-testid={`da-gallery-delete-${item.id}`}
              >
                <Trash2 className="h-3 w-3" />
              </button>
              <div className="p-2 space-y-1">
                <div className="text-xs font-medium truncate" title={item.nome}>{item.nome || "—"}</div>
                <div className="text-[10px] text-gray-500">{new Date(item.created_at).toLocaleDateString("it-IT")}</div>
                <div className="flex gap-1 mt-1">
                  {item.is_360 && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-amber-400 text-amber-700 hover:bg-amber-50 text-xs h-7 px-2"
                      onClick={() => openPreview(item, true)}
                      title="Anteprima navigabile 360°"
                      data-testid={`da-gallery-360-${item.id}`}
                    >
                      <Play className="h-3 w-3" />
                    </Button>
                  )}
                  <Button
                    size="sm"
                    className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-xs h-7"
                    onClick={() => onRefine(item.id)}
                    data-testid={`da-gallery-refine-${item.id}`}
                  >
                    <Wand2 className="h-3 w-3 mr-1" /> Rifinisci
                  </Button>
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="max-w-4xl" data-testid="da-gallery-preview">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ImageIcon className="h-5 w-5 text-indigo-600" /> {preview?.nome || "Anteprima"}
            </DialogTitle>
            {preview?.prompt_used && (
              <DialogDescription className="text-xs italic">Prompt: {preview.prompt_used.slice(0, 240)}{preview.prompt_used.length > 240 ? "..." : ""}</DialogDescription>
            )}
          </DialogHeader>
          <div className="flex justify-center bg-slate-900 rounded-lg overflow-hidden items-center" style={{ minHeight: 300, height: preview360 ? 520 : "auto" }}>
            {loadingFull || !preview?.image_base64 ? (
              <Loader2 className="h-10 w-10 animate-spin text-white" />
            ) : preview360 && preview.is_360 ? (
              <ReactPhotoSphereViewer
                key={`gpreview-${preview.id}`}
                src={preview.image_base64}
                height="100%"
                width="100%"
                littlePlanetButton={false}
                defaultZoomLvl={0}
                minFov={30}
                maxFov={110}
                navbar={["autorotate", "zoom", "move", "fullscreen"]}
              />
            ) : (
              <img src={preview.image_base64} alt={preview.nome} className="max-h-[65vh] object-contain" />
            )}
          </div>
          <DialogFooter className="border-t pt-3 flex-wrap gap-2">
            <Button variant="outline" onClick={() => setPreview(null)}>Chiudi</Button>
            {preview && (
              <>
                {preview.is_360 && (
                  <Button
                    variant="outline"
                    className={`border-amber-400 ${preview360 ? "bg-amber-50 text-amber-800" : "text-amber-700"}`}
                    onClick={() => setPreview360(v => !v)}
                    data-testid="da-gallery-preview-toggle-360"
                  >
                    <Globe className="h-4 w-4 mr-1" /> {preview360 ? "Vista piatta" : "Vista 360°"}
                  </Button>
                )}
                <Button
                  variant="outline"
                  className="text-red-600 border-red-300"
                  onClick={() => { onDelete(preview.id); setPreview(null); }}
                  data-testid="da-gallery-preview-delete"
                >
                  <Trash2 className="h-4 w-4 mr-1" /> Elimina
                </Button>
                <Button
                  className="bg-indigo-600 hover:bg-indigo-700"
                  onClick={() => { onRefine(preview.id); setPreview(null); }}
                  data-testid="da-gallery-preview-refine"
                >
                  <Wand2 className="h-4 w-4 mr-1" /> Rifinisci in nuovo Progetto
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// ================== BATCH RENDER PANEL ==================
function BatchRenderPanel({ authToken, fotoBase, fotoAggiuntive, promptDescrizione, foto360, riferimentiStile, quality: parentQuality, preserveText: parentPreserveText, preservationMask }) {
  const [batchOpen, setBatchOpen] = useState(false);
  const [batchPhotos, setBatchPhotos] = useState([]);  // [{nome, image_base64}]
  const [batchPrompt, setBatchPrompt] = useState("");
  const [createTour, setCreateTour] = useState(true);
  const [tourName, setTourName] = useState("");
  const [autoOpenWizard, setAutoOpenWizard] = useState(false);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  // Local overrides for the batch (inherit from parent, editable)
  const [batchQuality, setBatchQuality] = useState(parentQuality || "standard");
  const [batchPreserveText, setBatchPreserveText] = useState(parentPreserveText ?? true);
  const [useContextViews, setUseContextViews] = useState(true);
  const [useStyleRefs, setUseStyleRefs] = useState(true);
  const [useMask, setUseMask] = useState(!!preservationMask);
  const fileRef = useRef(null);

  // Prefill with foto_base + foto_aggiuntive when opening
  const openBatch = () => {
    const seed = [];
    if (fotoBase) seed.push({ nome: "Foto base", image_base64: fotoBase });
    (fotoAggiuntive || []).forEach((f, i) => seed.push({ nome: `Foto ${i + 2}`, image_base64: typeof f === "string" ? f : f.content_base64 || f.data }));
    setBatchPhotos(seed.slice(0, 4));
    setBatchPrompt(promptDescrizione || "");
    // Proposed tour name (editable): first ~40 chars of prompt + timestamp
    const stamp = new Date().toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
    const base = (promptDescrizione || "").trim().slice(0, 40);
    setTourName(base ? `Batch: ${base}${(promptDescrizione || "").length > 40 ? "…" : ""} · ${stamp}` : `Batch del ${stamp}`);
    // Sync inheritable options with parent state at open time
    setBatchQuality(parentQuality || "standard");
    setBatchPreserveText(parentPreserveText ?? true);
    setUseMask(!!preservationMask);
    setUseContextViews(true);
    setUseStyleRefs(true);
    setResult(null);
    setBatchOpen(true);
  };

  const addFiles = async (files) => {
    if (!files) return;
    for (const f of Array.from(files)) {
      if (batchPhotos.length >= 4) { toast.error("Massimo 4 immagini"); return; }
      const b64 = await new Promise((res) => {
        const r = new FileReader();
        r.onload = e => res(e.target.result);
        r.readAsDataURL(f);
      });
      setBatchPhotos(prev => [...prev, { nome: f.name.replace(/\.[^.]+$/, ""), image_base64: b64 }].slice(0, 4));
    }
  };
  const removeAt = (idx) => setBatchPhotos(prev => prev.filter((_, i) => i !== idx));
  const rename = (idx, val) => setBatchPhotos(prev => prev.map((x, i) => i === idx ? { ...x, nome: val } : x));

  const run = async () => {
    if (batchPhotos.length < 2) { toast.error("Servono almeno 2 immagini"); return; }
    if (!batchPrompt.trim()) { toast.error("Inserisci un prompt di base"); return; }
    setRunning(true); setResult(null);
    try {
      // Compress each photo client-side to avoid Cloudflare 520 on huge payloads
      const compressedPhotos = await Promise.all(
        batchPhotos.map(async (p) => {
          const compressed = await compressImageDataUrl(p.image_base64, { maxWidth: 2000, quality: 0.8 });
          return { ...p, image_base64: compressed };
        })
      );
      // Style refs (compressed)
      let styleRefsList = [];
      if (useStyleRefs && Array.isArray(riferimentiStile)) {
        styleRefsList = await Promise.all(
          riferimentiStile.map(async (r) => compressImageDataUrl(r.content_base64 || r, { maxWidth: 1600, quality: 0.78 }))
        );
      }
      // Context views (foto_aggiuntive) — extract data, compress
      let contextViews = [];
      if (useContextViews && Array.isArray(fotoAggiuntive)) {
        contextViews = await Promise.all(
          fotoAggiuntive.map(async (f) => {
            const dat = typeof f === "string" ? f : (f.content_base64 || f.data || "");
            return dat ? await compressImageDataUrl(dat, { maxWidth: 1600, quality: 0.78 }) : "";
          })
        );
        contextViews = contextViews.filter(Boolean);
      }
      // Preservation mask (already a small canvas dataURL; keep as-is)
      const maskB64 = useMask ? (preservationMask || null) : null;

      const totalBytes = compressedPhotos.reduce((acc, p) => acc + b64Size(p.image_base64), 0)
        + styleRefsList.reduce((a, x) => a + b64Size(x), 0)
        + contextViews.reduce((a, x) => a + b64Size(x), 0);
      if (totalBytes > 24 * 1024 * 1024) {
        toast.error("Immagini troppo pesanti anche dopo compressione. Riduci il numero o la risoluzione.");
        setRunning(false);
        return;
      }
      const body = {
        photos: compressedPhotos,
        prompt: batchPrompt,
        is_360: foto360,
        create_tour: createTour,
        tour_name: createTour ? tourName.trim() : "",
        // Full parity with single render
        quality: batchQuality,
        preserve_text: batchPreserveText,
        preservation_mask_base64: maskB64,
        foto_aggiuntive: contextViews,
        riferimenti_stile: styleRefsList,
      };
      const r = await axios.post(`${API}/design/batch-render-to-panorami?token=${authToken}`, body, { timeout: 300000 });
      setResult(r.data);
      window.dispatchEvent(new Event("wallet:refresh"));
      toast.success(`Batch completato: ${r.data.panorami.length} panorami creati${r.data.tour_created ? " + tour" : ""}${r.data.failures ? ` (${r.data.failures} falliti)` : ""}`);
      // Auto-open the wizard on Panorami page if requested
      if (autoOpenWizard && r.data.tour_created && r.data.tour_id) {
        setTimeout(() => {
          window.location.href = `${window.location.origin}${window.location.pathname}?section=panorami&openTour=${r.data.tour_id}`;
        }, 1500);
      }
    } catch (e) {
      const d = e.response?.data?.detail || e.message || "Errore batch";
      if (e.response?.status === 402) { toast.error(d); window.dispatchEvent(new Event("wallet:open-recharge")); }
      else toast.error(d);
    } finally { setRunning(false); }
  };

  const baseCosts = { bozza: 0.03, standard: 0.05, alta: 0.08, ultra: 0.15, ultra_pro: 0.25 };
  const perRender = (baseCosts[batchQuality] || 0.05)
    + (foto360 ? 0.01 : 0)
    + ((useContextViews ? (fotoAggiuntive?.length || 0) : 0) + (useStyleRefs ? (riferimentiStile?.length || 0) : 0)) * 0.015;
  const cost = (perRender * Math.max(batchPhotos.length, 0)).toFixed(2);

  return (
    <>
      <Button onClick={openBatch} variant="outline" className="w-full border-indigo-300 text-indigo-700 hover:bg-indigo-50" data-testid="da-batch-open-btn">
        <Sparkles className="h-4 w-4 mr-1" /> Batch: genera 2-4 render con stesso prompt + auto-tour
      </Button>

      <Dialog open={batchOpen} onOpenChange={(o) => !running && setBatchOpen(o)}>
        <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col" data-testid="da-batch-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Sparkles className="h-5 w-5 text-indigo-600" /> Batch Render → Tour Panorami 360°</DialogTitle>
            <DialogDescription>Carica 2-4 foto (stanze, angoli), un unico prompt: l&apos;AI produce un render per ciascuna e le collega in un tour navigabile.</DialogDescription>
          </DialogHeader>
          <div className="overflow-y-auto flex-1 space-y-3 py-2">
            <div>
              <Label>Foto base ({batchPhotos.length}/4)</Label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2">
                {batchPhotos.map((p, i) => (
                  <div key={i} className="relative border rounded overflow-hidden">
                    <img src={p.image_base64} alt={p.nome} className="w-full aspect-[4/3] object-cover" />
                    <Input value={p.nome} onChange={e => rename(i, e.target.value)} className="text-xs h-7 rounded-none border-0 border-t" />
                    <button type="button" onClick={() => removeAt(i)} className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1">
                      <X className="h-3 w-3" />
                    </button>
                    <Badge className="absolute top-1 left-1 bg-indigo-600 text-white text-[10px]">{i + 1}</Badge>
                  </div>
                ))}
                {batchPhotos.length < 4 && (
                  <button type="button" onClick={() => fileRef.current?.click()} className="border-2 border-dashed border-indigo-300 rounded aspect-[4/3] flex flex-col items-center justify-center text-indigo-600 hover:bg-indigo-50" data-testid="da-batch-add">
                    <Plus className="h-6 w-6" /><span className="text-xs mt-1">Aggiungi foto</span>
                  </button>
                )}
              </div>
              <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={e => { addFiles(e.target.files); e.target.value = ""; }} />
            </div>
            <div>
              <Label>Prompt di base (uguale per tutte)</Label>
              <Textarea value={batchPrompt} onChange={e => setBatchPrompt(e.target.value)} rows={3} placeholder="es. Stile scandinavo minimalista, tavolozza chiara, luci calde, mobili in legno naturale" data-testid="da-batch-prompt" />
            </div>

            {/* Quality tier + parity options */}
            <div className="rounded-lg border-2 border-purple-200 bg-gradient-to-br from-purple-50 to-indigo-50 p-3 space-y-2">
              <Label className="text-sm font-semibold text-purple-800 flex items-center gap-1">
                <Sparkles className="h-3.5 w-3.5" /> Parametri render (applicati a tutte le foto)
              </Label>
              <div>
                <Label className="text-xs">Qualità</Label>
                <select
                  value={batchQuality}
                  onChange={e => setBatchQuality(e.target.value)}
                  className="w-full mt-1 h-9 px-2 rounded-md border border-purple-200 bg-white text-sm"
                  data-testid="da-batch-quality"
                >
                  <option value="bozza">Bozza (~0.03 €/render — velocissimo)</option>
                  <option value="standard">Standard (~0.05 €/render — Nano Banana)</option>
                  <option value="alta">Alta (~0.08 €/render — Nano Banana +res)</option>
                  <option value="ultra">Ultra (~0.15 €/render — Nano Banana premium)</option>
                  <option value="ultra_pro">Ultra Pro (~0.25 €/render — Gemini 3 Pro)</option>
                </select>
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={batchPreserveText} onChange={e => setBatchPreserveText(e.target.checked)} data-testid="da-batch-preserve-text" />
                  Preserva testi/loghi
                </label>
                <label className="flex items-center gap-2 cursor-pointer" title={preservationMask ? "Riutilizza maschera disegnata nel render singolo" : "Nessuna maschera disegnata"}>
                  <input type="checkbox" checked={useMask && !!preservationMask} onChange={e => setUseMask(e.target.checked)} disabled={!preservationMask} data-testid="da-batch-use-mask" />
                  Maschera preservazione {preservationMask ? "" : "(non presente)"}
                </label>
                <label className="flex items-center gap-2 cursor-pointer" title={(fotoAggiuntive?.length || 0) > 0 ? `Riutilizza ${fotoAggiuntive.length} foto di contesto` : "Nessuna foto aggiuntiva"}>
                  <input type="checkbox" checked={useContextViews && (fotoAggiuntive?.length || 0) > 0} onChange={e => setUseContextViews(e.target.checked)} disabled={(fotoAggiuntive?.length || 0) === 0} data-testid="da-batch-use-context" />
                  Foto contesto ({fotoAggiuntive?.length || 0})
                </label>
                <label className="flex items-center gap-2 cursor-pointer" title={(riferimentiStile?.length || 0) > 0 ? `Riutilizza ${riferimentiStile.length} riferimenti stile` : "Nessun riferimento stile"}>
                  <input type="checkbox" checked={useStyleRefs && (riferimentiStile?.length || 0) > 0} onChange={e => setUseStyleRefs(e.target.checked)} disabled={(riferimentiStile?.length || 0) === 0} data-testid="da-batch-use-style" />
                  Riferimenti stile ({riferimentiStile?.length || 0})
                </label>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <input type="checkbox" id="da-batch-tour" checked={createTour} onChange={e => setCreateTour(e.target.checked)} data-testid="da-batch-tour" />
              <label htmlFor="da-batch-tour" className="text-sm">Collega automaticamente i panorami in tour (verrà salvato come <em>Tour</em> riapribile dal wizard)</label>
            </div>
            {createTour && (
              <div className="rounded-lg border-2 border-indigo-300 bg-gradient-to-br from-indigo-50 to-purple-50 p-3">
                <Label className="text-sm font-semibold text-indigo-800 flex items-center gap-1">
                  <Route className="h-4 w-4" /> Nome del tour <span className="text-red-500">*</span>
                </Label>
                <Input
                  value={tourName}
                  onChange={e => setTourName(e.target.value)}
                  placeholder="Es. Tour cucina villa Rossi"
                  className="mt-1 bg-white/80 border-indigo-200"
                  data-testid="da-batch-tour-name"
                />
                <p className="text-[11px] text-indigo-700/80 mt-1 italic">Il nome è editabile. Il tour comparirà nella card <strong>&quot;Tour salvati&quot;</strong> della sezione Panorami 360°.</p>
                <div className="flex items-center gap-2 mt-2 pt-2 border-t border-indigo-200">
                  <input type="checkbox" id="da-batch-auto-open" checked={autoOpenWizard} onChange={e => setAutoOpenWizard(e.target.checked)} data-testid="da-batch-auto-open" />
                  <label htmlFor="da-batch-auto-open" className="text-xs text-indigo-800 cursor-pointer">
                    🚀 <strong>Apri il wizard tour dopo il salvataggio</strong> (per rifinire subito ordine, viewpoint, hotspot)
                  </label>
                </div>
              </div>
            )}
            {foto360 && <p className="text-xs text-indigo-600">✓ Modalità 360° attiva — verranno generati panorami equirettangolari</p>}
            <div className="bg-amber-50 border border-amber-200 rounded p-2 text-xs text-amber-900">
              <strong>Costo stimato:</strong> {cost} Crediti ({batchPhotos.length} render × {perRender.toFixed(3)} Crediti/cad — qualità <strong>{batchQuality.replace("_", " ").toUpperCase()}</strong>{foto360 ? " + 360°" : ""})
            </div>
            {running && <AiProgressBar active={running} phases={[{ until: 15, label: "Preparazione batch..." }, { until: 40, label: `Rendering foto 1/${batchPhotos.length}...` }, { until: 80, label: "Rendering successivi..." }, { until: 100, label: "Creazione tour..." }]} testid="da-batch-progress" />}
            {result && (
              <div className="bg-emerald-50 border border-emerald-200 rounded p-3 text-sm">
                <div className="font-medium text-emerald-900 mb-1">✅ Batch completato</div>
                <div className="text-xs text-emerald-800">Creati {result.panorami.length} panorami{result.tour_created ? " + tour navigabile" : ""}{result.failures ? ` (${result.failures} render falliti — crediti rimborsati)` : ""}.</div>
                {result.tour_created && result.tour_id && (
                  <div className="text-xs text-emerald-900 mt-1">
                    🗺️ Tour salvato in <strong>Panorami 360° → Tour salvati</strong>. Da lì puoi rieditarlo (ordine, punti di vista, nome) come qualsiasi altro tour.
                  </div>
                )}
                <div className="flex flex-wrap gap-2 mt-2">
                  <Button size="sm" variant="outline" onClick={() => window.location.href = `${window.location.origin}${window.location.pathname}?panorama=${result.panorami[0].id}`} data-testid="da-batch-open-tour">
                    Avvia il tour →
                  </Button>
                  {result.tour_created && (
                    <Button size="sm" variant="outline" className="border-indigo-300 text-indigo-700" onClick={() => window.location.href = `${window.location.origin}${window.location.pathname}?section=panorami`} data-testid="da-batch-goto-panorami">
                      <Route className="h-3 w-3 mr-1" /> Vai a Panorami
                    </Button>
                  )}
                </div>
              </div>
            )}
          </div>
          <DialogFooter className="border-t pt-3">
            <Button variant="outline" onClick={() => setBatchOpen(false)} disabled={running}>Chiudi</Button>
            <Button onClick={run} disabled={running || batchPhotos.length < 2 || !batchPrompt.trim()} className="bg-gradient-to-r from-indigo-600 to-purple-600" data-testid="da-batch-run">
              {running ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Generazione...</> : <><Sparkles className="h-4 w-4 mr-1" /> Avvia batch ({batchPhotos.length} render)</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
